import React, {useEffect, useMemo, useRef, useState} from 'react';
import * as Sentry from '@sentry/browser';
import {injectIntl} from 'react-intl';
import CustomScrollDiv from '../../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import Util from '../../../../forecast-app/shared/util/util';
import moment from 'moment';
import {cloneDeep} from 'lodash';

import {BUDGET_TYPE, BUTTON_COLOR, BUTTON_STYLE, ELEMENT_TYPE, ESTIMATION_UNIT, PERIOD_BUDGET_TYPE, TABLE_TABS} from '../../../../constants';
import HeaderBar, {buildThirdLevelNavHeader} from '../../../../forecast-app/shared/components/headers/header-bar/header_bar';
import BudgetTable from './components/budget_table/budget_table';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import ExpandIcon from '../../../../images/v2/components/dropdown/expand_icon';
import BudgetCharts from './components/budget_charts';
import TablesSection from './components/tables_section';
import GenericEmptyState from '../../../empty-state/generic_empty_state_v2';
import FixedPriceContainer from './components/fixed_price_container';
import {ProjectFinancials} from './components/ProjectFinancials';
import * as tracking from '../../../../tracking';
import {BUDGET_CHART_TRACKING_LABELS, TABLE_TAB_TRACKING_LABELS} from './budget_constants';
import {add, createMilestoneExpenseCategoryVariance, createMilestoneRoleVariance, subtract} from './budget_data_utils';
import {calculatePeriodLockIndexes, getLockedAmountTotal} from './components/budget_table/period_locks';
import {BudgetTooltips} from './BudgetTooltips';
import FixedPriceUpdateToast from './components/fixed_price_update_toast';
import FinancialServiceAvailabilityToast from './components/financial_data_availability_toast';
import {PERMISSION_TYPE as PERMISSION} from "../../../../Permissions";
import {hasPermission} from "../../../../forecast-app/shared/util/PermissionsUtil";
import {hasFeatureFlag} from "../../../../forecast-app/shared/util/FeatureUtil";

const ProjectPage = ({
	intl,
	intl: {formatMessage, formatNumber},
	retry,
	hasFinancials,
	hasBaseline,
	viewer,
	viewer: {
		company,
		project,
		permissions,
		project: {
			financialDataAvailability,
			minutesPerEstimationPoint,
			estimationUnit,
			remainingCost,
			reported,
			remaining,
			forecastTotal,
			taskCostCalculationType,
			unassignedTaskHourlyCost,
			expenseItems,
			projectEndDay,
			projectEndMonth,
			projectEndYear,
			projectStartDay,
			projectStartMonth,
			projectStartYear,
			projectFirstDateYear,
			projectFirstDateMonth,
			projectFirstDateDay,
			projectLastDateYear,
			projectLastDateMonth,
			projectLastDateDay,
			costActualPerDay,
			priceActualPerDay,
			costPlannedPerDay,
			pricePlannedPerDay,
			costRemainingPerDay,
			priceRemainingPerDay,
			registeredMinutesPerDay,
			remainingMinutesPerDay,
			plannedMinutesPerDay,
			costPlannedWorkPerDay,
			pricePlannedWorkPerDay,
			costRemainingWorkPerDay,
			priceRemainingWorkPerDay,
			costActualWorkPerDay,
			priceActualWorkPerDay,
			pricePlannedExpensesPerDay,
			costPlannedExpensesPerDay,
			priceActualExpensesPerDay,
			costActualExpensesPerDay,
			priceRemainingExpensesPerDay,
			costRemainingExpensesPerDay,
			invoicedPerDay,
			financialServiceUnInvoicedTotal,
			totalPaymentsPerDay,
			breakdownBaselineMinutesPerDay,
			breakdownCostForecastPerDay: breakdownCostPlanPerDay,
			breakdownCostRemainingPerDay,
			breakdownCostActualPerDay,
			breakdownPricePlannedPerDay: breakdownPricePlanPerDay,
			breakdownPriceRemainingPerDay,
			breakdownPriceActualPerDay,
			breakdownCostForecastWorkPerDay: breakdownCostPlanWorkPerDay,
			breakdownCostRemainingWorkPerDay,
			breakdownCostActualWorkPerDay,
			breakdownPricePlannedWorkPerDay: breakdownPricePlanWorkPerDay,
			breakdownPriceRemainingWorkPerDay,
			breakdownPriceActualWorkPerDay,
			breakdownForecastMinutesPerDay: breakdownPlanMinutesPerDay,
			breakdownRemainingMinutesPerDay,
			breakdownActualMinutesPerDay,
			breakdownBaselinePricePerDay,
			breakdownBaselineCostPerDay,
			breakdownBaselineExpensesPricePerDay,
			breakdownExpensesPlannedPricePerDay: breakdownExpensesPlanPricePerDay,
			breakdownExpensesActualsPricePerDay,
			breakdownExpensesRemainingPricePerDay,
			breakdownBaselineExpensesCostPerDay,
			breakdownExpensesPlannedCostPerDay: breakdownExpensesPlanCostPerDay,
			breakdownExpensesActualsCostPerDay,
			breakdownExpensesRemainingCostPerDay,
			baselinePricePerDay,
			baselineCostPerDay,
			baselineCost,
			baselineTargetMinutes,
			baselineProfit,
			company: {
				roles: {edges: roles},
				expenseCategories: {edges: expenseCategories},
			},
			phases: {edges: milestones},
			fixedPriceLocks: {edges: locks},
		},
	},
}) => {
	// CLONING OF IMMUTABLE VIEWER OBJECTS
	const breakdownBaselinePricePerDayClone = cloneDeep(breakdownBaselinePricePerDay);
	const breakdownBaselineCostPerDayClone = cloneDeep(breakdownBaselineCostPerDay);
	const breakdownBaselineMinutesPerDayClone = cloneDeep(breakdownBaselineMinutesPerDay);
	const breakdownBaselineExpensesPricePerDayClone = cloneDeep(breakdownBaselineExpensesPricePerDay);
	const breakdownBaselineExpensesCostPerDayClone = cloneDeep(breakdownBaselineExpensesCostPerDay);

	const breakdownCostPlanPerDayClone = cloneDeep(breakdownCostPlanPerDay);
	const breakdownCostRemainingPerDayClone = cloneDeep(breakdownCostRemainingPerDay);
	const breakdownCostActualPerDayClone = cloneDeep(breakdownCostActualPerDay);

	const breakdownPricePlanPerDayClone = cloneDeep(breakdownPricePlanPerDay);
	const breakdownPriceRemainingPerDayClone = cloneDeep(breakdownPriceRemainingPerDay);
	const breakdownPriceActualPerDayClone = cloneDeep(breakdownPriceActualPerDay);

	const breakdownCostPlanWorkPerDayClone = cloneDeep(breakdownCostPlanWorkPerDay);
	const breakdownCostRemainingWorkPerDayClone = cloneDeep(breakdownCostRemainingWorkPerDay);
	const breakdownCostActualWorkPerDayClone = cloneDeep(breakdownCostActualWorkPerDay);

	const breakdownPricePlanWorkPerDayClone = cloneDeep(breakdownPricePlanWorkPerDay);
	const breakdownPriceRemainingWorkPerDayClone = cloneDeep(breakdownPriceRemainingWorkPerDay);
	const breakdownPriceActualWorkPerDayClone = cloneDeep(breakdownPriceActualWorkPerDay);

	const breakdownPlanMinutesPerDayClone = cloneDeep(breakdownPlanMinutesPerDay);
	const breakdownRemainingMinutesPerDayClone = cloneDeep(breakdownRemainingMinutesPerDay);
	const breakdownActualMinutesPerDayClone = cloneDeep(breakdownActualMinutesPerDay);

	const breakdownExpensesPlanPricePerDayClone = cloneDeep(breakdownExpensesPlanPricePerDay);
	const breakdownExpensesActualsPricePerDayClone = cloneDeep(breakdownExpensesActualsPricePerDay);
	const breakdownExpensesRemainingPricePerDayClone = cloneDeep(breakdownExpensesRemainingPricePerDay);
	const breakdownExpensesPlanCostPerDayClone = cloneDeep(breakdownExpensesPlanCostPerDay);
	const breakdownExpensesActualsCostPerDayClone = cloneDeep(breakdownExpensesActualsCostPerDay);
	const breakdownExpensesRemainingCostPerDayClone = cloneDeep(breakdownExpensesRemainingCostPerDay);

	const baselinePriceWorkPerDay = [];
	const baselinePriceExpensesPerDay = [];
	const baselineCostWorkPerDay = [];
	const baselineCostExpensesPerDay = [];

	const useFixedPriceV2 = hasFeatureFlag('fixed_price_v2');
	const hasLimitedFinancialAccess =  !hasPermission(PERMISSION.VIEW_FINANCIAL_INFORMATION) && hasPermission(PERMISSION.VIEW_FINANCIAL_INFORMATION_REVENUE);

	const currency = project.rateCard ? project.rateCard.currency : company.currency;

	const [showFixedPriceUpdateToast, setShowFixedPriceUpdateToast] = useState(false);

	if (hasBaseline) {
		const breakdownBaselinePriceWorkMap = {};
		const breakdownBaselinePriceExpensesMap = {};
		const breakdownBaselineCostWorkMap = {};
		const breakdownBaselineCostExpensesMap = {};
		breakdownBaselinePricePerDay.forEach(milestone =>
			milestone.roleBreakdown.forEach(role => {
				if (role.roleId === 'expense') {
					role.breakdown.forEach(breakdownItem => {
						if (!breakdownBaselinePriceExpensesMap[breakdownItem.date]) {
							breakdownBaselinePriceExpensesMap[breakdownItem.date] = breakdownItem.value;
						} else {
							breakdownBaselinePriceExpensesMap[breakdownItem.date] += breakdownItem.value;
						}
					});
				} else {
					role.breakdown.forEach(breakdownItem => {
						if (!breakdownBaselinePriceWorkMap[breakdownItem.date]) {
							breakdownBaselinePriceWorkMap[breakdownItem.date] = breakdownItem.value;
						} else {
							breakdownBaselinePriceWorkMap[breakdownItem.date] += breakdownItem.value;
						}
					});
				}
			})
		);
		breakdownBaselineCostPerDay.forEach(milestone =>
			milestone.roleBreakdown.forEach(role => {
				if (role.roleId === 'expense') {
					role.breakdown.forEach(breakdownItem => {
						if (!breakdownBaselineCostExpensesMap[breakdownItem.date]) {
							breakdownBaselineCostExpensesMap[breakdownItem.date] = breakdownItem.value;
						} else {
							breakdownBaselineCostExpensesMap[breakdownItem.date] += breakdownItem.value;
						}
					});
				} else {
					role.breakdown.forEach(breakdownItem => {
						if (!breakdownBaselineCostWorkMap[breakdownItem.date]) {
							breakdownBaselineCostWorkMap[breakdownItem.date] = breakdownItem.value;
						} else {
							breakdownBaselineCostWorkMap[breakdownItem.date] += breakdownItem.value;
						}
					});
				}
			})
		);
		for (const [key, value] of Object.entries(breakdownBaselinePriceWorkMap)) {
			baselinePriceWorkPerDay.push({date: key, value: value});
		}
		for (const [key, value] of Object.entries(breakdownBaselinePriceExpensesMap)) {
			baselinePriceExpensesPerDay.push({date: key, value: value});
		}
		for (const [key, value] of Object.entries(breakdownBaselineCostWorkMap)) {
			baselineCostWorkPerDay.push({date: key, value: value});
		}
		for (const [key, value] of Object.entries(breakdownBaselineCostExpensesMap)) {
			baselineCostExpensesPerDay.push({date: key, value: value});
		}
	}

	// STATE
	const [isMoneyTabSelected, setSelectedMoneyTab] = useState(!hasFinancials ? false : localStorage.getItem('isMoneyTabSelectedInBudgetV2') === null ? true : JSON.parse(localStorage.getItem('isMoneyTabSelectedInBudgetV2')));
	const [areTablesOutOfScreen, setAreTablesOutOfScreen] = useState(false);
	const [shouldTotalsBeStuck, setShouldTotalsBeStuck] = useState(false);
	const [isTotalsExpanded, setIsTotalsExpanded] = useState(localStorage.getItem('isTotalsTableExpandedBudgetV2') === null ? true : JSON.parse(localStorage.getItem('isTotalsTableExpandedBudgetV2')));
	const [scrollTop, setScrollTop] = useState(0);
	const [projectTotalsTop, setProjectTotalsTop] = useState(null);
	const [headerTop, setHeaderTop] = useState(null);
	const [selectedTablesTab, setSelectedTablesTab] = useState(
		JSON.parse(localStorage.getItem('isMoneyTabSelectedInBudgetV2'))
			? localStorage.getItem('selectedTablesTabBudgetV2')
				? localStorage.getItem('selectedTablesTabBudgetV2')
				: TABLE_TABS.WORK_AND_EXPENSES
			: JSON.parse(localStorage.getItem('isMoneyTabSelectedInBudgetV2')) === null
			? TABLE_TABS.WORK_AND_EXPENSES
			: TABLE_TABS.WORK
	);
	const [tablesData, setTablesData] = useState({});
	const [todayPeriodIndex, setTodayPeriodIndex] = useState(null); //  value that stores the index of the period in the table which contains todays date
	const [showFixedPrice, setShowFixedPrice] = useState(false);
	const [disabledColumnIndexes, setDisabledColumnIndexes] = useState([]);
	const [lockedColumnIndexMap, setLockedColumnIndexMap] = useState(new Map());
	const [isTableDataLoading, setIsTableDataLoading] = useState(true);

	const isEstimatedInNewPoints = project.estimationUnit === 'POINTS';
	const isDoneOrHalted = project.status === 'DONE' || project.status === 'HALTED';
	const isFixedPrice = project.budgetType === BUDGET_TYPE.FIXED_PRICE || project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 || (project.budgetType === BUDGET_TYPE.RETAINER && project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE);
	const isFixedPriceV2 = project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;

	let optionsInLocalStorage = false;

	if (localStorage.getItem('budget_v2_selector_options')) {
		optionsInLocalStorage = JSON.parse(localStorage.getItem('budget_v2_selector_options'));
		// reset the selected options if the data is old
		if (optionsInLocalStorage.some(optionCat => optionCat.name === 'workTableDetails')) {
			localStorage.removeItem('budget_v2_selector_options');
			optionsInLocalStorage = false;
		}
	}

	const projectTotalsFilterOptionsLocal = optionsInLocalStorage ? Util.getNestedOptionsObject(optionsInLocalStorage, 'projectTotals') : false;
	const projectVariancesFilterOptionsLocal = optionsInLocalStorage ? Util.getNestedOptionsObject(optionsInLocalStorage, 'projectVariances') : false;
	const projectNumbersFilterOptionsLocal = optionsInLocalStorage && hasFinancials ? Util.getNestedOptionsObject(optionsInLocalStorage, 'projectNumbers') : false;
	const tableNumbersFilterOptionsLocal = optionsInLocalStorage && hasFinancials ? Util.getNestedOptionsObject(optionsInLocalStorage, 'tableNumbers') : false;
	const tableFilterOptionsLocal = optionsInLocalStorage ? Util.getNestedOptionsObject(optionsInLocalStorage, 'tables') : false;
	const tableVariancesFilterOptionsLocal = optionsInLocalStorage ? Util.getNestedOptionsObject(optionsInLocalStorage, 'tableVariances') : false;
	const tableDetailsFilterOptionsLocal = optionsInLocalStorage ? Util.getNestedOptionsObject(optionsInLocalStorage, 'tableDetails') : false;
	const budgetEyeOptions = [
		{
			name: 'projectTotals',
			checked: projectTotalsFilterOptionsLocal ? projectTotalsFilterOptionsLocal.projectTotals : true,
			disabled: false,
			translationId: 'project_budget.project_totals',
			nestedOptions: [
				hasBaseline ? {name: 'baseline', checked: projectTotalsFilterOptionsLocal && projectTotalsFilterOptionsLocal.baseline ? projectTotalsFilterOptionsLocal.baseline : true, disabled: false, translationId: 'baseline.baseline'} : null,
				((isFixedPrice && !isFixedPriceV2) || (project.budgetType === BUDGET_TYPE.RETAINER && project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE)) && hasFinancials
					? {name: 'fixedPrice', checked: projectTotalsFilterOptionsLocal ? projectTotalsFilterOptionsLocal.fixedPrice : true, disabled: false, translationId: 'common.fixed_price'}
					: null,
				{name: 'actual', checked: projectTotalsFilterOptionsLocal ? projectTotalsFilterOptionsLocal.actual : true, disabled: false, translationId: 'project_budget.actual'},
				isEstimatedInNewPoints ? null : {name: 'plan', checked: projectTotalsFilterOptionsLocal ? projectTotalsFilterOptionsLocal.plan : true, disabled: false, translationId: 'common.plan'},
				{name: 'remaining', checked: projectTotalsFilterOptionsLocal ? projectTotalsFilterOptionsLocal.remaining : true, disabled: false, translationId: 'common.remaining'},
				{name: 'forecast', checked: projectTotalsFilterOptionsLocal ? projectTotalsFilterOptionsLocal.forecast : true, disabled: false, translationId: 'common.forecast'},
			],
		},
		{
			name: 'projectVariances',
			checked: projectVariancesFilterOptionsLocal ? projectVariancesFilterOptionsLocal.projectVariances : false,
			disabled: false,
			translationId: 'project_budget.project_variances',
			nestedOptions: [
				isEstimatedInNewPoints
					? null
					: {
							name: 'actualVarianceToPlan',
							checked: projectVariancesFilterOptionsLocal ? projectVariancesFilterOptionsLocal.actualVarianceToPlan : false,
							disabled: false,
							translationId: 'project_budget.actual_vs_plan',
					  },
				isEstimatedInNewPoints
					? null
					: {
							name: 'forecastVarianceToPlan',
							checked: projectVariancesFilterOptionsLocal ? projectVariancesFilterOptionsLocal.forecastVarianceToPlan : false,
							translationId: 'project_budget.forecast_vs_plan',
					  },
				hasBaseline
					? {
							name: 'baselineVarianceToActuals',
							checked: projectVariancesFilterOptionsLocal && projectVariancesFilterOptionsLocal.baselineVarianceToActuals !== undefined ? projectVariancesFilterOptionsLocal.baselineVarianceToActuals : false,
							translationId: 'project_budget.baseline_vs_actuals',
					  }
					: null,
				hasBaseline
					? {
							name: 'baselineVarianceToForecast',
							checked: projectVariancesFilterOptionsLocal && projectVariancesFilterOptionsLocal.baselineVarianceToForecast !== undefined ? projectVariancesFilterOptionsLocal.baselineVarianceToForecast : false,
							translationId: 'project_budget.baseline_vs_forecast',
					  }
					: null,
			],
		},
		{
			name: 'tables',
			checked: tableFilterOptionsLocal ? tableFilterOptionsLocal.tables : true,
			disabled: false,
			translationId: 'common.tables',
			nestedOptions: [
				hasBaseline ? {name: 'baseline', checked: tableFilterOptionsLocal && tableFilterOptionsLocal.baseline !== undefined ? tableFilterOptionsLocal.baseline : true, disabled: false, translationId: 'baseline.baseline'} : null,
				{name: 'actuals', checked: tableFilterOptionsLocal ? tableFilterOptionsLocal.actuals : true, disabled: false, translationId: 'project_budget.actuals'},
				isEstimatedInNewPoints ? null : {name: 'plan', checked: tableFilterOptionsLocal ? tableFilterOptionsLocal.plan : true, disabled: false, translationId: 'common.plan'},
				{name: 'remaining', checked: tableFilterOptionsLocal ? tableFilterOptionsLocal.remaining : true, disabled: false, translationId: 'common.remaining'},
				{name: 'forecast', checked: tableFilterOptionsLocal ? tableFilterOptionsLocal.forecast : true, disabled: false, translationId: 'common.forecast'},
				project.billable ? {name: 'invoice', checked: tableFilterOptionsLocal ? tableFilterOptionsLocal.invoice : true, disabled: false, translationId: 'project_section.invoice'} : null,
			],
		},
		{
			name: 'tableVariances',
			checked: tableVariancesFilterOptionsLocal ? tableVariancesFilterOptionsLocal.tableVariances : false,
			disabled: false,
			translationId: 'project_budget.table_variances',
			nestedOptions: [
				isEstimatedInNewPoints
					? null
					: {
							name: 'actualVarianceToPlan',
							checked: tableVariancesFilterOptionsLocal ? tableVariancesFilterOptionsLocal.actualVarianceToPlan : false,
							disabled: false,
							translationId: 'project_budget.actual_vs_plan',
					  },
				isEstimatedInNewPoints
					? null
					: {
							name: 'forecastVarianceToPlan',
							checked: tableVariancesFilterOptionsLocal ? tableVariancesFilterOptionsLocal.forecastVarianceToPlan : false,
							disabled: false,
							translationId: 'project_budget.forecast_vs_plan',
					  },
				project.billable
					? {
							name: 'invoiceVarianceToActuals',
							checked: tableVariancesFilterOptionsLocal ? tableVariancesFilterOptionsLocal.invoiceVarianceToActuals : false,
							disabled: false,
							translationId: 'project_budget.invoice_vs_actual',
					  }
					: null,
				hasBaseline
					? {
							name: 'baselineVarianceToActuals',
							checked: tableVariancesFilterOptionsLocal && tableVariancesFilterOptionsLocal.baselineVarianceToActuals !== undefined ? tableVariancesFilterOptionsLocal.baselineVarianceToActuals : false,
							disabled: false,
							translationId: 'project_budget.baseline_vs_actuals',
					  }
					: null,
				hasBaseline
					? {
							name: 'baselineVarianceToForecast',
							checked: tableVariancesFilterOptionsLocal && tableVariancesFilterOptionsLocal.baselineVarianceToForecast !== undefined ? tableVariancesFilterOptionsLocal.baselineVarianceToForecast : false,
							disabled: false,
							translationId: 'project_budget.baseline_vs_forecast',
					  }
					: null,
			],
		},
		{
			name: 'tableDetails',
			checked: tableDetailsFilterOptionsLocal ? tableDetailsFilterOptionsLocal.tableDetails : false,
			disabled: false,
			translationId: 'project_budget.table_details',
			nestedOptions: [
				{name: 'milestone', checked: tableDetailsFilterOptionsLocal ? tableDetailsFilterOptionsLocal.milestone : false, disabled: false, translationId: 'common.milestone'}, //
				{name: 'role', checked: tableDetailsFilterOptionsLocal ? tableDetailsFilterOptionsLocal.role : false, disabled: false, translationId: 'project_budget.role_expense'},
				{
					name: 'projectEntries',
					checked: tableDetailsFilterOptionsLocal ? tableDetailsFilterOptionsLocal.projectEntries : false,
					disabled: false,
					translationId: 'project_budget.project_time_entries_with_no_tasks',
				},
			],
		},
	];

	const projectNumbers = {
		name: 'projectNumbers',
		checked: projectNumbersFilterOptionsLocal ? projectNumbersFilterOptionsLocal.projectNumbers : true,
		disabled: false,
		translationId: 'project_budget.project_numbers',
		nestedOptions: [
			project.billable ? {name: 'revenue', checked: projectNumbersFilterOptionsLocal ? projectNumbersFilterOptionsLocal.revenue : true, disabled: false, translationId: 'project_budget.revenue'} : null,
			hasLimitedFinancialAccess ? null : {name: 'cost', checked: projectNumbersFilterOptionsLocal ? projectNumbersFilterOptionsLocal.cost : true, disabled: false, translationId: 'common.cost'},
			(project.billable && !hasLimitedFinancialAccess) ? {name: 'profit', checked: projectNumbersFilterOptionsLocal ? projectNumbersFilterOptionsLocal.profit : true, disabled: false, translationId: 'common.profit'} : null,
			(project.billable && !hasLimitedFinancialAccess) ? {name: 'margin', checked: projectNumbersFilterOptionsLocal ? projectNumbersFilterOptionsLocal.margin : true, disabled: false, translationId: 'common.margin'} : null,
		],
	};

	const tableNumbers = {
		name: 'tableNumbers',
		checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.tableNumbers : true,
		disabled: false,
		translationId: 'project_budget.table_numbers',
		nestedOptions: [
			project.billable ? {name: 'revenue', checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.revenue : true, disabled: false, translationId: 'project_budget.revenue'} : null,
			hasLimitedFinancialAccess ? null : {name: 'cost', checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.cost : true, disabled: false, translationId: 'common.cost'},
			(project.billable && !hasLimitedFinancialAccess) ? {name: 'profit', checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.profit : true, disabled: false, translationId: 'common.profit'} : null,
			project.billable
				? {
						name: 'invoiced',
						checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.invoiced : true,
						disabled: false,
						translationId: 'common.invoiced',
				  }
				: null,
			project.billable ? {name: 'paid', checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.paid : true, disabled: false, translationId: 'project_section.xero_invoice_paid'} : null,
			project.billable ? {name: 'accrued', checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.accrued : true, disabled: false, translationId: 'project_budget.accrued'} : null,
			project.billable
				? {
						name: 'accumulatedTotals',
						checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.accumulatedTotals : false,
						disabled: false,
						translationId: 'project_budget.accumulated_totals',
				  }
				: null,
			{name: 'weekNumber', checked: tableNumbersFilterOptionsLocal ? tableNumbersFilterOptionsLocal.weekNumber : false, disabled: false, translationId: 'project_budget.week_numbers'},
		],
	};

	if (hasFinancials) {
		const projectNumbersPushAtIndex = budgetEyeOptions.findIndex(col => col.name === 'projectVariances');
		budgetEyeOptions.splice(projectNumbersPushAtIndex + 1, 0, projectNumbers);
		const tableNumbersPushAtIndex = budgetEyeOptions.findIndex(col => col.name === 'tableVariances');
		budgetEyeOptions.splice(tableNumbersPushAtIndex + 1, 0, tableNumbers);
	}
	budgetEyeOptions.forEach((option, index) => {
		if (!option.nestedOptions.some(nestedOption => nestedOption !== null)) {
			budgetEyeOptions.splice(index, 1);
		}
	});

	const [theEyeOptions, setTheEyeOptions] = useState(budgetEyeOptions);
	const projectFirstDate = Util.CreateNonUtcMomentDate(projectFirstDateYear, projectFirstDateMonth, projectFirstDateDay);
	const projectLastDate = Util.CreateNonUtcMomentDate(projectLastDateYear, projectLastDateMonth, projectLastDateDay);
	const [selectedPeriod, setSelectedPeriod] = useState(localStorage.getItem(`selectedPeriodBudgetV2-project${project.companyProjectId}`));

	// REFS
	const tableContainerRef = useRef(null);
	const tablesSectionRef = useRef(null);
	const projectTotalsDiv = useRef(null);
	const headerRef = useRef(null);

	const projectTotalsFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'projectTotals');
	const projectVariancesFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'projectVariances');
	const projectNumbersFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'projectNumbers');
	const tableNumbersFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'tableNumbers');
	const tableDetailsFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'tableDetails');
	const missingProjectDates = !project.projectStartYear || !project.projectEndYear || !project.projectFirstDateYear || !project.projectLastDateYear;

	// function that returns the period a column in the table should represent
	const getDefaultColumnPeriod = (projectStartDate, projectEndDate) => {
		const projectPeriodLengthInDays = projectStartDate && projectEndDate ? projectEndDate.diff(projectStartDate, 'days') : 180;

		let periodInTable;

		if (projectPeriodLengthInDays < 31) {
			periodInTable = 'days';
		} else if (projectPeriodLengthInDays > 31 && projectPeriodLengthInDays < 62) {
			periodInTable = 'weeks';
		} else {
			periodInTable = 'months';
		}
		return periodInTable;
	};

	const getColumnPeriod = (projectStartDate, projectEndDate) => {
		if (selectedPeriod) {
			return selectedPeriod;
		}
		const periodInTable = getDefaultColumnPeriod(projectStartDate, projectEndDate);
		setSelectedPeriod(periodInTable);
		return periodInTable;
	};

	const projectStartDate = Util.CreateNonUtcMomentDate(projectStartYear, projectStartMonth, projectStartDay);
	const projectEndDate = Util.CreateNonUtcMomentDate(projectEndYear, projectEndMonth, projectEndDay);
	const periodInTable = !projectFirstDate && !projectLastDate ? '' : getColumnPeriod(projectFirstDate, projectLastDate);

	const dateCache = new Map();

	const getMomentDate = dateString => {
		if (dateCache.has(dateString)) {
			return dateCache.get(dateString);
		} else {
			const momentDate = moment(dateString);
			dateCache.set(dateString, momentDate);
			return momentDate;
		}
	};

	useEffect(() => {
		if (projectTotalsDiv && projectTotalsDiv.current) {
			const {top} = projectTotalsDiv.current.getBoundingClientRect();
			setProjectTotalsTop(top - 55);
		}

		if (headerRef && headerRef.current) {
			const {top} = headerRef.current.getBoundingClientRect();
			setHeaderTop(top);
		}

		requestAnimationFrame(() => {
			if (tableContainerRef && tableContainerRef.current) {
				setAreTablesOutOfScreen(tableContainerRef.current.getBoundingClientRect().width > window.innerWidth);
			}
		});
	}, []);

	// when we change the tab check if the totals should get stuck when scrolling
	useEffect(() => {
		requestAnimationFrame(() => {
			if (tablesSectionRef && tablesSectionRef.current) {
				setShouldTotalsBeStuck(
					// check if the scroll area is smaller than the tables height
					(tablesSectionRef.current ? tablesSectionRef.current.getBoundingClientRect().height : 0) >
						window.innerHeight -
							37 - //menu
							34 - //secondary menu
							(headerRef.current ? headerRef.current.getBoundingClientRect().height : 0) - //header
							(projectTotalsDiv.current ? projectTotalsDiv.current.getBoundingClientRect().height : 0) //totals table
				);
			}
		});
	}, [selectedTablesTab, isTableDataLoading]);

	// handle adding/removing fixed price
	useEffect(() => {
		let selectorOptions = theEyeOptions;
		const hasFixedPriceOption = selectorOptions.find(option => option.name === 'projectTotals').nestedOptions.find(nop => nop !== null && nop.name === 'fixedPrice');

		if (isFixedPrice) {
			// becasuse we saved in the localstorage the selector options before adding the fixed price option we need to check if it exists and add it
			if (!hasFixedPriceOption && !hasBaseline && !isFixedPriceV2) {
				// select the fixed price option if project previously did not have fixed price
				selectorOptions
					.find(option => option.name === 'projectTotals')
					.nestedOptions.unshift({
						name: 'fixedPrice',
						checked: true,
						disabled: false,
						translationId: 'common.fixed_price',
					});
			}
			setTheEyeOptions(selectorOptions);
		} else if (!isFixedPrice && hasFixedPriceOption) {
			// remove the fixed price option from the selector
			setTheEyeOptions(selectorOptions.map(option => (option.name === 'projectTotals' ? {...option, nestedOptions: option.nestedOptions.filter(opt => opt !== null && opt.name !== 'fixedPrice')} : option)));
		}
	}, [isFixedPrice]);

	const currencySymbol = Util.GetCurrencySymbol(currency);

	const getFormattedNumberWithCurrency = (currency, number) =>
		Util.getFormattedCurrencyValue(
			currency,
			formatNumber(Math.round(number > -0.005 && number < 0 ? 0 : number * 100) / 100, {minimumFractionDigits: 2, maximumFractionDigits: 2}) //compare the value with 0.005 to prevent math.round to return -0
		);

	const getFormattedNumberWithEstimationUnit = number => {
		if (estimationUnit !== ESTIMATION_UNIT.POINTS) {
			return Util.convertMinutesToFullHour(number, intl);
		} else {
			if (isEstimatedInNewPoints) {
				return Util.convertMinutesToFullHour(number, intl);
			} else {
				return formatNumber(Math.round((number / minutesPerEstimationPoint) * 100) / 100) + 'p';
			}
		}
	};

	// function that formats the values in the passed table data. It also adds the currency symbol
	const formatTableData = tableData => {
		const formatValue = value => {
			if (value === 0) {
				return '–';
			} else if (isMoneyTabSelected) {
				return isNaN(value) || value === '' ? value : getFormattedNumberWithCurrency(currencySymbol, value);
			} else {
				return getFormattedNumberWithEstimationUnit(value);
			}
		};

		if (tableData) {
			return tableData.map((row, rowIndex) => {
				if (rowIndex !== 0 && row !== null) {
					return row.map((cell, colIndex) => {
						if (colIndex !== 0 && cell !== null) {
							return cell.map(cellValue => {
								if (Array.isArray(cellValue)) {
									return cellValue.map(cellValueValue => {
										if (Array.isArray(cellValueValue)) {
											return cellValueValue.map(cellValueValueValue => {
												return formatValue(cellValueValueValue);
											});
										} else {
											return formatValue(cellValueValue);
										}
									});
								} else {
									return formatValue(cellValue);
								}
							});
						} else {
							return cell;
						}
					});
				} else {
					return row;
				}
			});
		}
		return [];
	};

	const calculateCellTotal = rowData => {
		if (rowData) {
			// rowData.length - 1 because we do not need to calculate the totals in the total column
			for (let i = 1; i < rowData.length - 1; i++) {
				// push one element to each cell to create a new line
				rowData[i].push(rowData.reduce((acc, curr, j) => acc + (j > 0 && j <= i ? curr[0] : 0), 0));
			}
		}
		return rowData;
	};

	// receives an array with the data for each row and another array with the rows names
	const getTableData = (tableName, rowsData, rowNames, tableWithProfit, dataHasBreakdown) => {
		if (!projectFirstDate || !projectLastDate || !projectFirstDate.isValid() || !projectLastDate.isValid()) return [];

		return new Promise((resolve, _reject) => {
			// at this point you might be wondering why has requestAnimationFrame been used in a page that is aleardy slow? to make the code asyncronous, therefore faster and user experience less laggy
			requestAnimationFrame(() => {
				let tableColumnNames = [[null]];
				let columnPeriods = [[null]]; // array that contains the start and end dates of each period represented in each column in the tables

				let currentDate = Util.CreateNonUtcMomentDate(projectFirstDate.year(), projectFirstDate.month() + 1, projectFirstDate.date());

				const disabledColumnIndexes = [];

				const todaysDate = moment();
				// creating column for the table based on the period length of the project
				// in order to avoid extra month added in the periodInTable array we are comparing the endOf endData and the startof the currentDate. moment.diff returns 1 if period is 15.11 - 15.12 and 2 if period is 15.11-16.12
				while (projectLastDate.clone().endOf(periodInTable).diff(currentDate.clone().startOf(periodInTable), periodInTable, true) > 0) {
					const startOfCurrentPeriod = currentDate.clone().startOf(periodInTable === 'days' ? 'day' : periodInTable === 'weeks' ? 'week' : 'month');
					const endOfCurrentPeriod = currentDate.clone().endOf(periodInTable === 'days' ? 'day' : periodInTable === 'weeks' ? 'week' : 'month');

					tableColumnNames.push(
						periodInTable === 'weeks'
							? !tableNumbersFilterOptions.weekNumber
								? [`${currentDate.clone().startOf('week').format('D MMM')} - ${currentDate.clone().endOf('week').format('DD MMM YYYY')}`]
								: [`${formatMessage({id: 'timeline.week'})} ${currentDate.format('w')}`, `${currentDate.clone().startOf('week').format('D MMM')} - ${currentDate.clone().endOf('week').format('DD MMM YYYY')}`]
							: [currentDate.format(periodInTable === 'months' ? 'MMMM YYYY' : 'DD MMM YYYY')]
					);

					columnPeriods.push([startOfCurrentPeriod, endOfCurrentPeriod]);

					if (todaysDate.isBetween(startOfCurrentPeriod, endOfCurrentPeriod, 'day', '[]')) {
						setTodayPeriodIndex(columnPeriods.length - 1);
					}

					if (startOfCurrentPeriod.isAfter(projectEndDate) || endOfCurrentPeriod.isBefore(projectStartDate)) {
						disabledColumnIndexes.push(columnPeriods.length - 1);
					}

					currentDate = currentDate.add(1, periodInTable);
				}

				if (disabledColumnIndexes) {
					setDisabledColumnIndexes(disabledColumnIndexes);
				}

				if (isFixedPriceV2) {
					const lockedColumnIndexMap = calculatePeriodLockIndexes(columnPeriods, locks);
					if (lockedColumnIndexMap) {
						setLockedColumnIndexMap(lockedColumnIndexMap);
					}
				}

				// add the last column, total
				tableColumnNames.push([formatMessage({id: 'common.total'})]);

				// add secondary text in the rows
				const isShowingTotals = tableNumbersFilterOptions.accumulatedTotals;

				// function that returns an array with all the data for each column based on the period shown in the column
				const getRowData = (rowName, data) => {
					let rowData;
					if (dataHasBreakdown) {
						// if we have use breakdown, return arrays for each level in the breakdown
						const breakdownRowNames = data.map(milestone => {
							if (milestone.milestoneId === 'expenses') {
								return {name: formatMessage({id: 'project_budget.expenses'}), shouldShow: tableDetailsFilterOptions.expenses};
							}
							if (milestone.milestoneId === 'project-time-regs') {
								return {name: formatMessage({id: 'project_budget.project_time_entries_with_no_tasks'}), shouldShow: tableDetailsFilterOptions.projectEntries};
							}
							if (milestone.milestoneId === 'offset') {
								return {name: formatMessage({id: 'common.offset'}), shouldShow: tableName === 'actualsDataPerDayMoney' && rowName === 'Revenue'};
							}

							let rowValues;
							if (milestone.roleBreakdown) {
								rowValues = milestone.roleBreakdown
									.filter(role => (role.roleId !== 'expense' || milestone.milestoneId !== 'expenses') && role.roleId !== 'project-time-regs')
									.map(role => {
										const roleData = roles.find(r => r.node.id === role.roleId);
										if (roleData) {
											return roleData.node.name;
										} else if (role.roleId === 'expense') {
											return formatMessage({id: 'project_budget.expenses'});
										} else if (role.roleId === 'not-planned') {
											return formatMessage({id: 'common.unplanned_capitalized'});
										} else {
											return formatMessage({id: 'team.no-role-group-title'});
										}
									});
							} else if (milestone.expenseCategoryBreakdown) {
								rowValues = milestone.expenseCategoryBreakdown.map(expenseCategory => {
									const expenseCategoryData = expenseCategories.find(ec => ec.node.id === expenseCategory.expenseCategoryId);
									return expenseCategoryData.node.name;
								});
							}

							return [milestones.some(m => m.node.id === milestone.milestoneId) ? milestones.find(m => m.node.id === milestone.milestoneId).node.name : formatMessage({id: 'card_modal.no-scope'}), rowValues];
						});

						breakdownRowNames.unshift(rowName);
						if (isShowingTotals) breakdownRowNames.push(formatMessage({id: 'common.acc_totals'}));
						rowData = [breakdownRowNames];
					} else {
						rowData = [isShowingTotals ? [rowName, formatMessage({id: 'common.acc_totals'})] : [rowName]];
					}

					for (let period of columnPeriods) {
						// get all values where date is between period start and end and calculate the total in the period
						if (period[0]) {
							let totalInPeriod;
							if (dataHasBreakdown) {
								let periodTotal = 0;
								let milestoneTotals = [];
								for (let i = 0; i < data.length; i++) {
									let milestoneTotal = 0;
									const roleBreakdown = data[i].roleBreakdown;
									const expenseCategoryBreakdown = data[i].expenseCategoryBreakdown;
									let groupingLevel2Totals = [];
									if (roleBreakdown) {
										const isExpenseMilestone = data[i].milestoneId === 'expenses';
										const isProjectTimeRegsMilestone = data[i].milestoneId === 'project-time-regs';
										const isOffset = data[i].milestoneId === 'offset';
										for (let j = 0; j < roleBreakdown.length; j++) {
											let roleTotal = 0;
											const isExpenseUnderMilestone = !isExpenseMilestone && roleBreakdown[j].roleId === 'expense';
											const isRoleValue = roleBreakdown[j].roleId !== 'expense';
											const showValue = isExpenseMilestone || isExpenseUnderMilestone || isRoleValue || isProjectTimeRegsMilestone || isOffset;
											const entries = roleBreakdown[j].breakdown;
											for (let k = 0; k < entries.length; k++) {
												let entry = entries[k];
												if (getMomentDate(entry.date).isBetween(period[0], period[1], 'day', '[]')) {
													periodTotal += entry.value;
													milestoneTotal += entry.value;
													if (showValue) {
														roleTotal += entry.value;
													}
												}
											}
											if (showValue) {
												groupingLevel2Totals.push(roleTotal);
											}
										}
									} else if (expenseCategoryBreakdown) {
										for (let j = 0; j < expenseCategoryBreakdown.length; j++) {
											let expenseCategoryTotal = 0;
											const entries = expenseCategoryBreakdown[j].breakdown;
											for (let k = 0; k < entries.length; k++) {
												let entry = entries[k];
												if (getMomentDate(entry.date).isBetween(period[0], period[1], 'day', '[]')) {
													periodTotal += entry.value;
													milestoneTotal += entry.value;
													expenseCategoryTotal += entry.value;
												}
											}
											groupingLevel2Totals.push(expenseCategoryTotal);
										}
									}
									if (data[i].milestoneId === 'expenses') {
										milestoneTotals.push(milestoneTotal);
									} else if (data[i].milestoneId === 'project-time-regs') {
										milestoneTotals.push(milestoneTotal);
									} else if (data[i].milestoneId === 'offset') {
										milestoneTotals.push(milestoneTotal);
									} else {
										milestoneTotals.push([milestoneTotal, groupingLevel2Totals]);
									}
								}
								const rowNumbers = [periodTotal, ...milestoneTotals];

								rowData.push(rowNumbers);
							} else {
								totalInPeriod = data.filter(entry => getMomentDate(entry.date).isBetween(period[0], period[1], 'day', '[]')).reduce((acc, curr) => acc + curr.value, 0);
								rowData.push([totalInPeriod]);
							}
						}
					}

					// add total in the last column of the table
					rowData.push([rowData.reduce((acc, curr, i) => acc + (i !== 0 ? curr[0] : 0), 0)]);

					// if totals are visible, add subtext in the row
					if (isShowingTotals) {
						rowData = calculateCellTotal(rowData);
					}
					return rowData;
				};

				// function that calculates the profit on each table. returns array with data for the profit row
				// this function subtracts from the first array values from the second array based on the value index. eg: a[2] - b[2]
				const getProfit = (tableA, tableB) => {
					let profitTable;
					if (selectedTablesTab === TABLE_TABS.EXPENSES) {
						profitTable = createMilestoneExpenseCategoryVariance(tableA, tableB, subtract);
					} else {
						profitTable = createMilestoneRoleVariance(tableA, tableB, subtract);
					}
					return getRowData(formatMessage({id: 'common.profit'}), profitTable);
				};

				if (isMoneyTabSelected) {
					// get data for the revenue and the cost rows
					let revenueRowData = project.billable ? getRowData(rowNames !== null && rowNames[0] ? rowNames[0] : formatMessage({id: 'project_budget.revenue'}), rowsData[0]) : null;
					//TODO T35081 filter cost
					let costRowData = getRowData(rowNames !== null && rowNames[1] ? rowNames[1] : formatMessage({id: 'common.cost'}), rowsData[1]);
					let profitRowData = tableNumbersFilterOptions.profit && tableWithProfit && project.billable ? getProfit(rowsData[0], rowsData[1]) : null;

					// return custom data for when invoice tab is selected
					if (selectedTablesTab === TABLE_TABS.INVOICE) {
						const revenueRowDataForAllData = getRowData(rowNames !== null && rowNames[0] ? rowNames[0] : formatMessage({id: 'project_budget.revenue'}), rowsData[0]);
						const profitRowDataForAllData = getProfit(rowsData[0], rowsData[1]);

						return resolve([
							// table data
							[
								tableColumnNames,
								selectedTablesTab === TABLE_TABS.INVOICE && tableNumbersFilterOptions.invoiced ? revenueRowData : null,
								selectedTablesTab === TABLE_TABS.INVOICE && tableNumbersFilterOptions.paid ? costRowData : null,
								profitRowData,
							],
							// all data
							[tableColumnNames, revenueRowDataForAllData, costRowData, profitRowDataForAllData],
						]);
					}

					return resolve([
						tableColumnNames,
						(selectedTablesTab === TABLE_TABS.INVOICE && tableNumbersFilterOptions.invoiced) || tableNumbersFilterOptions.revenue ? revenueRowData : null,
						tableNumbersFilterOptions.cost ? costRowData : null,
						profitRowData,
					]);
				} else if (!isMoneyTabSelected) {
					// return a table with a row with the name and the data
					return resolve([tableColumnNames, getRowData(rowNames !== null ? rowNames[0] : formatMessage({id: 'common.time_entries'}), rowsData[0])]);
				}
			});
		});
	};

	// this function takes 2 rows and returns a table that contains the passed rows and a third row that contains the result of the calculation
	const getInvoiceVarianceToActualTableData = (tableColumnNames, row1, row2, operation) => {
		return new Promise((resolve, _reject) => {
			// at this point you might be wandering why has requestAnimationFrame been used in a page that is aleardy slow? to make the code asyncronous, therefore faster and user experience less laggy
			requestAnimationFrame(() => {
				let x = [...row1];
				let y = [...row2];
				const isShowingTotals = tableNumbersFilterOptions.accumulatedTotals;

				const varianceRow = tableNumbersFilterOptions.accrued
					? x.map((cell, i) =>
							i === 0
								? isShowingTotals
									? [formatMessage({id: 'project_budget.accrued'}), formatMessage({id: 'common.acc_totals'})]
									: [formatMessage({id: 'project_budget.accrued'})]
								: operation === 'add'
								? [x[i][0] + y[i][0]]
								: [x[i][0] - y[i][0]]
					  )
					: null;

				let varianceTableData = [tableColumnNames, tableNumbersFilterOptions.invoiced ? x : null, tableNumbersFilterOptions.revenue ? y : null, varianceRow];

				if (isShowingTotals) {
					varianceTableData.map((row, i) => (i !== 0 ? calculateCellTotal(row) : row));
					// add totals text in the row name cell
					varianceTableData.map((row, i) => (i !== 0 && row !== null ? (row[0][1] = formatMessage({id: 'common.acc_totals'})) : row));
				}

				return resolve(varianceTableData);
			});
		});
	};

	const handleTheEyeOptionSelect = (_, __, ___, newOptions) => {
		setTheEyeOptions(newOptions);
		setIsTableDataLoading(true);

		let locallyStoredOptions = JSON.parse(localStorage.getItem('budget_v2_selector_options'));
		const newEyeOptions = locallyStoredOptions ? Object.assign(locallyStoredOptions, newOptions) : newOptions;
		Util.localStorageSetItem('budget_v2_selector_options', JSON.stringify(newEyeOptions));
	};

	const showCostRateModal = () => {
		showModal({
			type: MODAL_TYPE.COST_RATE,
			currencySymbol,
			taskCostCalculationType,
			unassignedTaskHourlyCost,
			project,
		});

		tracking.trackElementClicked('Change Cost Rate button');
	};

	const toggleFixedPrice = () => {
		setShowFixedPrice(!showFixedPrice);

		if (!showFixedPrice) {
			tracking.trackElementClicked('Edit Fixed Price button');
		}
	};

	const showFixedPriceModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_FIXED_PRICE,
			currency,
			isFixedPrice,
			useFixedPriceV2,
			project,
			getFormattedNumberWithCurrency,
			callback: function () {
				setShowFixedPriceUpdateToast(true);
			},
		});

		tracking.trackElementClicked('Edit Fix Price button');
	};

	const showUpdateFixedPriceModal = () => {
		showModal({
			type: MODAL_TYPE.UPDATE_FIXED_PRICE,
			project,
			callback: function () {
				setShowFixedPriceUpdateToast(true);
			},
		});

		tracking.trackElementClicked('Update Fix Price button');
	};

	const getHeader = () => {
		const leftContent = [],
			rightContent = [];

		if (hasFinancials) {
			leftContent.push({
				type: ELEMENT_TYPE.UNIT_TOGGLE,
				options: [
					{
						text: formatMessage({id: 'common.money'}),
						onClick: () => {
							setSelectedMoneyTab(true);
							setIsTableDataLoading(true);
							Util.localStorageSetItem('isMoneyTabSelectedInBudgetV2', true);
							setSelectedTablesTab(localStorage.getItem('selectedTablesTabBudgetV2') ? localStorage.getItem('selectedTablesTabBudgetV2') : TABLE_TABS.WORK_AND_EXPENSES);

							tracking.trackElementClicked('Money Tab');
						},
						selected: isMoneyTabSelected,
						dataCy: 'money',
						userpilot: 'money-tab',
					},
					{
						text: formatMessage({id: 'project-economic.th_time'}),
						onClick: () => {
							setSelectedMoneyTab(false);
							setIsTableDataLoading(true);
							Util.localStorageSetItem('isMoneyTabSelectedInBudgetV2', false);
							setSelectedTablesTab(TABLE_TABS.WORK);

							tracking.trackElementClicked('Time Tab');
						},
						selected: !isMoneyTabSelected,
						dataCy: 'time',
						userpilot: 'time-tab',
					},
				],
			});
		} else {
			leftContent.push({
				type: ELEMENT_TYPE.UNIT_TOGGLE,
				options: [
					{
						text: formatMessage({id: 'project-economic.th_time'}),
						onClick: () => {
							setSelectedMoneyTab(false);
							setIsTableDataLoading(true);
							Util.localStorageSetItem('isMoneyTabSelectedInBudgetV2', false);
							setSelectedTablesTab(TABLE_TABS.WORK);

							tracking.trackElementClicked('Time Tab');
						},
						selected: !isMoneyTabSelected,
						dataCy: 'time',
						userpilot: 'time-tab',
					},
					{
						text: formatMessage({id: 'common.money'}),
						onClick: () => null,
						selected: isMoneyTabSelected,
						dataCy: 'money',
						userpilot: 'money-tab',
						locked: true,
						tooltipProps: {infoText: formatMessage({id: 'common.requires_financial_module'})},
					},
				],
			});
		}

		leftContent.push({
			type: ELEMENT_TYPE.THE_EYE,
			options: theEyeOptions,
			onSelect: handleTheEyeOptionSelect,
			openRight: true,
		});

		if (project && isDoneOrHalted) {
			const indicator = {
				type: ELEMENT_TYPE.INDICATOR,
				status: project.status,
			};
			leftContent.push(indicator);
		}

		const projectPeriodLengthInDays = !projectFirstDate || !projectLastDate ? 0 : projectLastDate.diff(projectFirstDate, 'days');

		let periodOptions = ['weeks', 'months'];
		if (projectPeriodLengthInDays < 180) periodOptions.unshift('days');
		const defaultPeriod = getDefaultColumnPeriod(projectFirstDate, projectLastDate);
		const defaultText = formatMessage({id: 'settings.rate_card_default'});
		const defaultSuffix = ` (${defaultText.charAt(0).toLowerCase()}${defaultText.slice(1)})`;
		periodOptions = periodOptions.map(option => {
			return {value: option, label: `${formatMessage({id: `common.${option}`})}${option === defaultPeriod ? defaultSuffix : ''}`};
		});

		const selectPeriod = period => {
			const value = period ? period.value : '';
			setSelectedPeriod(value);
			setIsTableDataLoading(true);
			Util.localStorageSetItem(`selectedPeriodBudgetV2-project${project.companyProjectId}`, value);
		};

		leftContent.push({
			type: ELEMENT_TYPE.LEFT_DROPDOWN,
			dropdownOptions: periodOptions,
			value: selectedPeriod,
			callback: selectPeriod,
			placeholder: formatMessage({id: 'project_budget.select_period'}),
			userpilot: 'budget-period-dropdown',
		});

		if (!hasFeatureFlag('third_level_nav')) {
			leftContent.push({
				type: ELEMENT_TYPE.HELP_LOGO,
				href: 'https://help.forecast.it/project-budget',
			});
		}

		const budgetRelease = Util.CreateNonUtcMomentDate(2019, 11, 5);
		const now = Util.GetYearMonthDateFromMomentDate(moment());
		const today = Util.CreateNonUtcMomentDate(now.year, now.month, now.day);
		if (today.isBefore(budgetRelease)) {
			leftContent.push({
				type: ELEMENT_TYPE.BETA_TOOLTIP,
			});
		}

		const useNewFixedPriceUI = (project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 || project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS) && useFixedPriceV2;

		if (hasFinancials) {
			if (project.billable && project.budgetType !== BUDGET_TYPE.RETAINER) {
				rightContent.push({
					type: ELEMENT_TYPE.BUTTON,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.PURPLE,
					text: isFixedPrice ? formatMessage({id: 'project_budget.edit_fixed_price'}) : formatMessage({id: 'project_budget.set_fixed_price'}),
					disabled: isDoneOrHalted,
					callback: useNewFixedPriceUI ? showFixedPriceModal : toggleFixedPrice,
					dataCy: 'button-fixed-price',
				});
			}

			if (project.budgetType === BUDGET_TYPE.FIXED_PRICE && useFixedPriceV2) {
				rightContent.push({
					type: ELEMENT_TYPE.BUTTON,
					style: BUTTON_STYLE.OUTLINE,
					color: BUTTON_COLOR.PURPLE,
					text: formatMessage({id: 'project_budget.update_fixed_price'}),
					disabled: isDoneOrHalted,
					callback: showUpdateFixedPriceModal,
					dataCy: 'button-update-fixed-price',
				});
			}

			!useFixedPriceV2 &&
				rightContent.push({
					type: ELEMENT_TYPE.BUTTON,
					style: BUTTON_STYLE.OUTLINE_THICK,
					color: BUTTON_COLOR.VERYLIGHTGREY,
					text: formatMessage({id: 'project_budget.change_cost_rate'}),
					disabled: isDoneOrHalted,
					callback: showCostRateModal,
					userpilot: 'change-cost-rate-button',
				});
		}

		if (hasFeatureFlag('third_level_nav')) {
			return buildThirdLevelNavHeader(leftContent, rightContent, null, {innerRef: headerRef});
		}

		return <HeaderBar innerRef={headerRef} parentGroup={null} leftContent={leftContent} rightContent={rightContent} />;
	};

	const getTotalsTableData = () => {
		// Totals Calculations

		let actualRevenue = priceActualPerDay.reduce((total, acc) => total + acc.value, 0);

		let actualCost = costActualPerDay.reduce((total, acc) => total + acc.value, 0);

		let plannedRevenue = pricePlannedPerDay.reduce((total, acc) => total + acc.value, 0);

		let plannedCost = costPlannedPerDay.reduce((total, acc) => total + acc.value, 0);

		let remainingRevenue = priceRemainingPerDay.reduce((total, acc) => total + acc.value, 0);

		let remainingWithExpensesCost = costRemainingPerDay.reduce((total, acc) => total + acc.value, 0);

		let fixedPriceRevenue, fixedPriceCost, fixedPriceProfit;
		if (isFixedPrice) {
			fixedPriceRevenue = project.budget;
			fixedPriceCost = actualCost + remainingCost;
			fixedPriceProfit = fixedPriceRevenue - fixedPriceCost;
		}
		const rowTitles = [
			[null],
			hasBaseline && projectTotalsFilterOptions.baseline ? [formatMessage({id: 'baseline.baseline'})] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToActuals ? [formatMessage({id: 'project_budget.baseline_vs'}), formatMessage({id: 'project_budget.actuals'})] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToForecast ? [formatMessage({id: 'project_budget.baseline_vs'}), formatMessage({id: 'common.forecast'})] : null,
			isFixedPrice && !isFixedPriceV2 && projectTotalsFilterOptions.fixedPrice ? [formatMessage({id: 'common.fixed_price'})] : null,
			projectTotalsFilterOptions.actual ? [formatMessage({id: 'project_budget.actuals'})] : null,
			projectTotalsFilterOptions.plan ? [formatMessage({id: 'common.plan'})] : null,
			projectVariancesFilterOptions.actualVarianceToPlan ? [formatMessage({id: 'project_budget.actual_vs_plan'})] : null,
			projectTotalsFilterOptions.remaining ? [formatMessage({id: 'cards.remaining'})] : null,
			projectTotalsFilterOptions.forecast ? [formatMessage({id: 'common.forecast'})] : null,
			projectVariancesFilterOptions.forecastVarianceToPlan ? [formatMessage({id: 'project_budget.forecast_vs_plan'})] : null,
		];
		const revenueRow = [
			[formatMessage({id: 'project_budget.revenue'})],
			hasBaseline && projectTotalsFilterOptions.baseline ? [baselineCost + baselineProfit] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToActuals ? [baselineCost + baselineProfit - actualRevenue] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToForecast ? [baselineCost + baselineProfit - (actualRevenue + remainingRevenue)] : null,
			isFixedPrice && !isFixedPriceV2 && projectTotalsFilterOptions.fixedPrice ? [fixedPriceRevenue] : null,
			projectTotalsFilterOptions.actual ? [actualRevenue] : null,
			projectTotalsFilterOptions.plan ? [plannedRevenue] : null,
			projectVariancesFilterOptions.actualVarianceToPlan ? [actualRevenue - plannedRevenue] : null,
			projectTotalsFilterOptions.remaining ? [remainingRevenue] : null,
			projectTotalsFilterOptions.forecast ? [actualRevenue + remainingRevenue] : null,
			projectVariancesFilterOptions.forecastVarianceToPlan ? [actualRevenue + remainingRevenue - plannedRevenue] : null,
		];
		const costRow = [
			[formatMessage({id: 'insights.component.list.column.cost'})],
			hasBaseline && projectTotalsFilterOptions.baseline ? [baselineCost] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToActuals ? [baselineCost - actualCost] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToForecast ? [baselineCost - (actualCost + remainingCost)] : null,
			isFixedPrice && !isFixedPriceV2 && projectTotalsFilterOptions.fixedPrice ? [fixedPriceCost] : null,
			projectTotalsFilterOptions.actual ? [actualCost] : null,
			projectTotalsFilterOptions.plan ? [plannedCost] : null,
			projectVariancesFilterOptions.actualVarianceToPlan ? [actualCost - plannedCost] : null,
			projectTotalsFilterOptions.remaining ? [remainingWithExpensesCost] : null,
			projectTotalsFilterOptions.forecast ? [actualCost + remainingCost] : null,
			projectVariancesFilterOptions.forecastVarianceToPlan ? [actualCost + remainingWithExpensesCost - plannedCost] : null,
		];
		const profitRow = [
			[formatMessage({id: 'insights.component.list.column.profit'})],
			hasBaseline && projectTotalsFilterOptions.baseline ? [baselineProfit] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToActuals ? [baselineProfit - (actualRevenue - actualCost)] : null,
			hasBaseline && projectVariancesFilterOptionsLocal.baselineVarianceToForecast ? [baselineProfit - (actualRevenue + remainingRevenue - actualCost - remainingCost)] : null,
			isFixedPrice && !isFixedPriceV2 && projectTotalsFilterOptions.fixedPrice ? [fixedPriceProfit] : null,
			projectTotalsFilterOptions.actual ? [actualRevenue - actualCost] : null,
			projectTotalsFilterOptions.plan ? [plannedRevenue - plannedCost] : null,
			projectVariancesFilterOptions.actualVarianceToPlan ? [actualRevenue - plannedRevenue - actualCost + plannedCost] : null,
			projectTotalsFilterOptions.remaining ? [remainingRevenue - remainingWithExpensesCost] : null,
			projectTotalsFilterOptions.forecast ? [actualRevenue + remainingRevenue - actualCost - remainingCost] : null,
			projectVariancesFilterOptions.forecastVarianceToPlan ? [actualRevenue + remainingRevenue - plannedRevenue - actualCost - remainingWithExpensesCost + plannedCost] : null,
		];

		const marginRow = [[formatMessage({id: 'common.margin'})]];

		const varianceIndexes = [2, 3, 7, 10];
		profitRow.forEach((profit, index) => {
			if (index !== 0) {
				if (profit && revenueRow[index] && !isNaN(profit[0]) && !isNaN(revenueRow[index][0])) {
					if (varianceIndexes.includes(index)) {
						marginRow.push(['']);
					} else {
						const margin = Math.round((profit[0] / revenueRow[index][0]) * 100);
						isNaN(margin) || !isFinite(margin) ? marginRow.push([0]) : marginRow.push([margin + ' %']);
					}
				} else {
					marginRow.push(null);
				}
			}
		});

		const totalsTableData = isMoneyTabSelected
			? [
					rowTitles,
					project.billable && projectNumbersFilterOptions.revenue ? revenueRow : null,
					projectNumbersFilterOptions.cost ? costRow : null,
					projectNumbersFilterOptions.profit && project.billable ? profitRow : null,
					projectNumbersFilterOptions.margin ? marginRow : null,
			  ]
			: [
					[
						[null],
						hasBaseline && projectTotalsFilterOptions.baseline ? [formatMessage({id: 'baseline.baseline'})] : null,
						hasBaseline && projectVariancesFilterOptions.baselineVarianceToActuals ? [formatMessage({id: 'project_budget.baseline_vs'}), formatMessage({id: 'project_budget.actuals'})] : null,
						hasBaseline && projectVariancesFilterOptions.baselineVarianceToForecast ? [formatMessage({id: 'project_budget.baseline_vs'}), formatMessage({id: 'common.forecast'})] : null,
						projectTotalsFilterOptions.actual ? [formatMessage({id: 'project_budget.actuals'})] : null,
						projectTotalsFilterOptions.plan ? [formatMessage({id: 'common.plan'})] : null,
						projectVariancesFilterOptions.actualVarianceToPlan ? [formatMessage({id: 'project_budget.actual_vs_plan'})] : null,
						projectTotalsFilterOptions.remaining ? [formatMessage({id: 'cards.remaining'})] : null,
						projectTotalsFilterOptions.forecast ? [formatMessage({id: 'common.forecast'})] : null,
						projectVariancesFilterOptions.forecastVarianceToPlan ? [formatMessage({id: 'project_budget.forecast_vs_plan'})] : null,
					],
					[
						[formatMessage({id: 'project-economic.th_time'})],
						hasBaseline && projectTotalsFilterOptions.baseline ? [baselineTargetMinutes] : null,
						hasBaseline && projectVariancesFilterOptions.baselineVarianceToActuals ? [baselineTargetMinutes - reported] : null,
						hasBaseline && projectVariancesFilterOptions.baselineVarianceToForecast ? [baselineTargetMinutes - (remaining * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1) + reported)] : null,
						projectTotalsFilterOptions.actual ? [reported] : null,
						projectTotalsFilterOptions.plan ? [forecastTotal * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1)] : null, // forecastTotal comes in points/minutes from the backend so we transform it here in minutes before transforming it back in hours/points in the formatTableData
						projectVariancesFilterOptions.actualVarianceToPlan ? [reported - forecastTotal * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1)] : null,
						projectTotalsFilterOptions.remaining ? [remaining * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1)] : null,
						projectTotalsFilterOptions.forecast ? [remaining * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1) + reported] : null,
						projectVariancesFilterOptions.forecastVarianceToPlan
							? [remaining * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1) + reported - forecastTotal * (estimationUnit === ESTIMATION_UNIT.POINTS ? minutesPerEstimationPoint : 1)]
							: null,
					],
			  ];
		return totalsTableData;
	};

	const showFixedPriceTooltip = isFixedPriceV2 && project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const actualsTooltipText = BudgetTooltips.ActualTooltips(formatMessage, isMoneyTabSelected, showFixedPriceTooltip, selectedTablesTab);
	const actualsTooltipTextTotals = BudgetTooltips.ActualTooltips(formatMessage, isMoneyTabSelected, showFixedPriceTooltip, null);
	const planTooltipText = BudgetTooltips.PlanTooltips(formatMessage, isMoneyTabSelected, showFixedPriceTooltip, selectedTablesTab);
	const planTooltipTextTotals = BudgetTooltips.PlanTooltips(formatMessage, isMoneyTabSelected, showFixedPriceTooltip, null);
	const remainingTooltipText = BudgetTooltips.RemainingTooltips(formatMessage, isMoneyTabSelected, showFixedPriceTooltip, selectedTablesTab);
	const remainingTooltipTextTotals = BudgetTooltips.RemainingTooltips(formatMessage, isMoneyTabSelected, showFixedPriceTooltip, null);
	const forecastTooltipText = BudgetTooltips.ForecastTooltips(formatMessage, isMoneyTabSelected);
	const forecastTooltipTextTotals = BudgetTooltips.ForecastTooltips(formatMessage, isMoneyTabSelected);
	const actualVarianceToPlanTooltipText = BudgetTooltips.ActualVsPlanTooltips(formatMessage, isMoneyTabSelected);
	const actualVarianceToPlanTooltipTextTotals = BudgetTooltips.ActualVsPlanTooltips(formatMessage, isMoneyTabSelected);
	const forecastVarianceToPlanTooltipText = BudgetTooltips.ForecastVsPlanTooltips(formatMessage, isMoneyTabSelected);
	const forecastVarianceToPlanTooltipTextTotals = BudgetTooltips.ForecastVsPlanTooltips(formatMessage, isMoneyTabSelected);
	const fixedPriceProjectFinancialsTooltip = BudgetTooltips.FixedPriceProjectFinancialsTooltip(formatMessage);
	const fixedPriceTooltipTextTotals = BudgetTooltips.FixedPriceTooltips(formatMessage);
	const baselineTooltipText = BudgetTooltips.BaselineTooltips(formatMessage, isMoneyTabSelected, selectedTablesTab);
	const baselineTooltipTextTotals = BudgetTooltips.BaselineTooltips(formatMessage, isMoneyTabSelected, null);
	const baselineVarianceToActualsTooltipText = BudgetTooltips.BaselineVsActualsTooltips(formatMessage, isMoneyTabSelected);
	const baselineVarianceToActualsTooltipTextTotals = BudgetTooltips.BaselineVsActualsTooltips(formatMessage, isMoneyTabSelected);
	const baselineVarianceToForecastTooltipText = BudgetTooltips.BaselineVsForecastTooltips(formatMessage, isMoneyTabSelected);
	const baselineVarianceToForecastTooltipTextTotals = BudgetTooltips.BaselineVsForecastTooltips(formatMessage, isMoneyTabSelected);
	const invoiceVarianceToActualTooltipText = BudgetTooltips.InvoicedVsActualsTooltips(formatMessage, isMoneyTabSelected);

	const handleScroll = data => {
		// prevent unecessary state updates
		if (data.scrollTop + 80 < 1000) {
			setScrollTop(data.scrollTop + 80);
		}
	};

	const toggleTotals = () => {
		setIsTotalsExpanded(!isTotalsExpanded);
		Util.localStorageSetItem('isTotalsTableExpandedBudgetV2', !isTotalsExpanded);

		if (isTotalsExpanded) {
			tracking.trackElementClicked('Project totals:Collapsed');
		} else {
			tracking.trackElementClicked('Project totals:Expanded');
		}
	};

	const handleTablesTabSelected = tab => {
		setSelectedTablesTab(tab);
		setIsTableDataLoading(true);
		Util.localStorageSetItem('selectedTablesTabBudgetV2', tab);
		Util.localStorageSetItem('isMoneyTabSelectedInBudgetV2', isMoneyTabSelected);

		if (TABLE_TAB_TRACKING_LABELS[tab]) {
			tracking.trackElementClicked(TABLE_TAB_TRACKING_LABELS[tab], {
				Placement: 'Tables',
			});
		}
	};

	const getBreakdownInSortedOrder = tableData => {
		// sort milestones chronologically, then no-milestone, then expenses and lastly project level time regs
		const dateToNumber = momentDate => {
			return momentDate === null ? 0 : momentDate.valueOf();
		};

		const specialMilestoneIds = ['no-milestone', 'expenses', 'project-time-regs', 'offset'];
		tableData.sort((a, b) => {
			let sortOrder = 0;

			const aIsSpecialMilestone = specialMilestoneIds.indexOf(a.milestoneId) > -1;
			const bIsSpecialMilestone = specialMilestoneIds.indexOf(b.milestoneId) > -1;

			// Sort milestones chronologically against each other
			if (!aIsSpecialMilestone && !bIsSpecialMilestone && milestones.find(milestone => milestone.node.id === a.milestoneId) && milestones.find(milestone => milestone.node.id === b.milestoneId)) {
				const milestoneA = milestones.find(milestone => milestone.node.id === a.milestoneId).node;
				const milestoneB = milestones.find(milestone => milestone.node.id === b.milestoneId).node;

				// First look at the start date
				const milestoneAStart = dateToNumber(Util.CreateNonUtcMomentDate(milestoneA.startYear, milestoneA.startMonth, milestoneA.startDay));
				const milestoneBStart = dateToNumber(Util.CreateNonUtcMomentDate(milestoneB.startYear, milestoneB.startMonth, milestoneB.startDay));

				sortOrder = milestoneAStart.valueOf() - milestoneBStart.valueOf();

				// If start dates are equal look at end date
				if (sortOrder === 0) {
					const milestoneADeadline = dateToNumber(Util.CreateNonUtcMomentDate(milestoneA.deadlineYear, milestoneA.deadlineMonth, milestoneA.deadlineDay));
					const milestoneBDeadline = dateToNumber(Util.CreateNonUtcMomentDate(milestoneB.deadlineYear, milestoneB.deadlineMonth, milestoneB.deadlineDay));

					sortOrder = milestoneADeadline.valueOf() - milestoneBDeadline.valueOf();
				}

				// If two milestones have equal periods (either real dates or no dates), sort by name
				if (sortOrder === 0) {
					if (milestoneA.name < milestoneB.name) {
						sortOrder = -1;
					} else if (milestoneA.name > milestoneB.name) {
						sortOrder = 1;
					}
				}
			} else if (aIsSpecialMilestone && !bIsSpecialMilestone) {
				// Sort special milestones below real milestones
				sortOrder = 1;
			} else if (bIsSpecialMilestone && !aIsSpecialMilestone) {
				// Sort special milestones below real milestones
				sortOrder = -1;
			} else {
				// Sort special milestones based on order in the specialMilestoneIds array
				sortOrder = specialMilestoneIds.indexOf(a.milestoneId) - specialMilestoneIds.indexOf(b.milestoneId);
			}
			return sortOrder;
		});

		tableData.forEach(milestone => {
			const getRoleName = roleId => {
				if (roleId === 'no-role') {
					return 'No role';
				} else if (roleId === 'expense') {
					return 'Expense';
				} else if (roleId === 'project-time-regs') {
					return 'Project level time regs';
				} else if (roleId === 'not-planned') {
					return 'ZZZZ Not planned';
				} else {
					const role = roles.find(r => r.node.id === roleId);
					return role ? role.node.name : '';
				}
			};

			const getExpenseCategoryName = expenseCategoryId => {
				const expenseCategory = expenseCategories.find(expenseCategory => expenseCategory.node.id === expenseCategoryId);
				return expenseCategory ? expenseCategory.node.name : '';
			};

			// sort roles in each milestone alphabetically
			if (milestone.roleBreakdown) {
				milestone.roleBreakdown.sort((a, b) => (getRoleName(a.roleId) < getRoleName(b.roleId) ? -1 : 1));
			}
			// Sort expense categories alphabetically
			if (milestone.expenseCategoryBreakdown) {
				milestone.expenseCategoryBreakdown.sort((a, b) => (getExpenseCategoryName(a.expenseCategoryId) < getExpenseCategoryName(b.expenseCategoryId) ? -1 : 1));
			}

			return milestone;
		});
		return tableData;
	};

	const filterRoles = (roles, roleFilter) => {
		const filteredRoles = [];
		roles.forEach(role => {
			if (roleFilter === undefined || roleFilter(role)) {
				filteredRoles.push({
					roleId: role.roleId,
					breakdown: role.breakdown,
				});
			}
		});
		return filteredRoles;
	};

	const filterMilestones = (milestones, milestoneFilter, roleFilter) => {
		const filteredMilestones = [];
		milestones.forEach(milestone => {
			if (milestoneFilter === undefined || milestoneFilter(milestone)) {
				const filteredRoles = filterRoles(milestone.roleBreakdown, roleFilter);
				if (filteredRoles.length > 0) {
					filteredMilestones.push({
						milestoneId: milestone.milestoneId,
						roleBreakdown: filteredRoles,
					});
				}
			}
		});
		return filteredMilestones;
	};

	// Hook where we update the data in the tables when necessary
	useEffect(() => {
		// console.clear();
		let baselineDataPerDay = [];
		let baselineVsActualsDataPerDay = [];
		let baselineVsForecastDataPerDay = [];
		let actualsDataPerDay = [];
		let planDataPerDay = [];
		let remainingDataPerDay = [];
		let forecastDataPerDay = [];
		let actualsVsPlanDataPerDay = [];
		let forecastVsPlanDataPerDay = [];
		let invoicedDataPerDay = [];

		switch (selectedTablesTab) {
			case TABLE_TABS.WORK:
				if (isMoneyTabSelected) {
					const breakdownPriceWorkForecastPerDay = createMilestoneRoleVariance(breakdownPriceActualWorkPerDayClone, breakdownPriceRemainingWorkPerDayClone, add);
					const breakdownCostWorkForecastPerDay = createMilestoneRoleVariance(breakdownCostActualWorkPerDayClone, breakdownCostRemainingWorkPerDayClone, add);
					const breakdownPriceWorkActualVsPlanPerDay = createMilestoneRoleVariance(breakdownPriceActualWorkPerDayClone, breakdownPricePlanWorkPerDayClone, subtract);
					const breakdownCostWorkActualVsPlanPerDay = createMilestoneRoleVariance(breakdownCostActualWorkPerDayClone, breakdownCostPlanWorkPerDayClone, subtract);
					const breakdownPriceWorkForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownPriceWorkForecastPerDay, breakdownPricePlanWorkPerDayClone, subtract);
					const breakdownCostWorkForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownCostWorkForecastPerDay, breakdownCostPlanWorkPerDayClone, subtract);
					actualsDataPerDay = [getBreakdownInSortedOrder(breakdownPriceActualWorkPerDayClone), getBreakdownInSortedOrder(breakdownCostActualWorkPerDayClone)];
					planDataPerDay = [getBreakdownInSortedOrder(breakdownPricePlanWorkPerDayClone), getBreakdownInSortedOrder(breakdownCostPlanWorkPerDayClone)];
					remainingDataPerDay = [getBreakdownInSortedOrder(breakdownPriceRemainingWorkPerDayClone), getBreakdownInSortedOrder(breakdownCostRemainingWorkPerDayClone)];
					forecastDataPerDay = [getBreakdownInSortedOrder(breakdownPriceWorkForecastPerDay), getBreakdownInSortedOrder(breakdownCostWorkForecastPerDay)];
					actualsVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownPriceWorkActualVsPlanPerDay), getBreakdownInSortedOrder(breakdownCostWorkActualVsPlanPerDay)];
					forecastVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownPriceWorkForecastVsPlanPerDay), getBreakdownInSortedOrder(breakdownCostWorkForecastVsPlanPerDay)];
					if (hasBaseline) {
						const breakdownBaselinePriceWorkPerDay = filterMilestones(
							breakdownBaselinePricePerDayClone,
							milestone => milestone.milestoneId !== 'expenses',
							role => role.roleId !== 'expense'
						);
						const breakdownBaselineCostWorkPerDay = filterMilestones(
							breakdownBaselineCostPerDayClone,
							milestone => milestone.milestoneId !== 'expenses',
							role => role.roleId !== 'expense'
						);
						const breakdownPriceBaselineVsActualWorkPerDay = createMilestoneRoleVariance(breakdownBaselinePriceWorkPerDay, breakdownPriceActualWorkPerDayClone, subtract);
						const breakdownCostBaselineVsActualWorkPerDay = createMilestoneRoleVariance(breakdownBaselineCostWorkPerDay, breakdownCostActualWorkPerDayClone, subtract);
						const breakdownPriceWorkBaselineVsForecastPerDay = createMilestoneRoleVariance(breakdownBaselinePriceWorkPerDay, breakdownPriceWorkForecastPerDay, subtract);
						const breakdownCostWorkBaselineVsForecastPerDay = createMilestoneRoleVariance(breakdownBaselineCostWorkPerDay, breakdownCostWorkForecastPerDay, subtract);
						baselineDataPerDay = [getBreakdownInSortedOrder(breakdownBaselinePriceWorkPerDay), getBreakdownInSortedOrder(breakdownBaselineCostWorkPerDay)];
						baselineVsActualsDataPerDay = [getBreakdownInSortedOrder(breakdownPriceBaselineVsActualWorkPerDay), getBreakdownInSortedOrder(breakdownCostBaselineVsActualWorkPerDay)];
						baselineVsForecastDataPerDay = [getBreakdownInSortedOrder(breakdownPriceWorkBaselineVsForecastPerDay), getBreakdownInSortedOrder(breakdownCostWorkBaselineVsForecastPerDay)];
					}
				} else {
					const breakdownMinutesForecastPerDay = createMilestoneRoleVariance(breakdownActualMinutesPerDayClone, breakdownRemainingMinutesPerDayClone, add);
					const breakdownMinutesActualVsPlanPerDay = createMilestoneRoleVariance(breakdownActualMinutesPerDayClone, breakdownPlanMinutesPerDayClone, subtract);
					const breakdownMinutesForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownMinutesForecastPerDay, breakdownPlanMinutesPerDayClone, subtract);
					actualsDataPerDay = [getBreakdownInSortedOrder(breakdownActualMinutesPerDayClone)];
					planDataPerDay = [getBreakdownInSortedOrder(breakdownPlanMinutesPerDayClone)];
					remainingDataPerDay = [getBreakdownInSortedOrder(breakdownRemainingMinutesPerDayClone)];
					forecastDataPerDay = [getBreakdownInSortedOrder(breakdownMinutesForecastPerDay)];
					actualsVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownMinutesActualVsPlanPerDay)];
					forecastVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownMinutesForecastVsPlanPerDay)];
					if (hasBaseline) {
						const breakdownMinutesBaselineVsActualPerDay = createMilestoneRoleVariance(breakdownBaselineMinutesPerDayClone, breakdownActualMinutesPerDayClone, subtract);
						const breakdownMinutesBaselineVsForecastPerDay = createMilestoneRoleVariance(breakdownBaselineMinutesPerDayClone, breakdownMinutesForecastPerDay, subtract);
						baselineDataPerDay = [getBreakdownInSortedOrder(breakdownBaselineMinutesPerDayClone)];
						baselineVsActualsDataPerDay = [getBreakdownInSortedOrder(breakdownMinutesBaselineVsActualPerDay)];
						baselineVsForecastDataPerDay = [getBreakdownInSortedOrder(breakdownMinutesBaselineVsForecastPerDay)];
					}
				}
				break;
			case TABLE_TABS.EXPENSES:
				const breakdownExpensesPlannedPricePerDay = createMilestoneExpenseCategoryVariance(breakdownExpensesActualsPricePerDayClone, breakdownExpensesRemainingPricePerDayClone, add);
				const breakdownExpensesPlannedCostPerDay = createMilestoneExpenseCategoryVariance(breakdownExpensesActualsCostPerDayClone, breakdownExpensesRemainingCostPerDayClone, add);
				const breakdownExpensesPriceActualVsPlanPerDay = createMilestoneRoleVariance(breakdownExpensesActualsPricePerDayClone, breakdownExpensesPlanPricePerDayClone, subtract);
				const breakdownExpensesCostActualVsPlanPerDay = createMilestoneRoleVariance(breakdownExpensesActualsCostPerDayClone, breakdownExpensesPlanCostPerDayClone, subtract);
				const breakdownExpensesPriceForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownExpensesPlannedPricePerDay, breakdownExpensesPlanPricePerDayClone, subtract);
				const breakdownExpensesCostForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownExpensesPlannedCostPerDay, breakdownExpensesPlanCostPerDayClone, subtract);
				actualsDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesActualsPricePerDayClone), getBreakdownInSortedOrder(breakdownExpensesActualsCostPerDayClone)];
				planDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesPlanPricePerDayClone), getBreakdownInSortedOrder(breakdownExpensesPlanCostPerDayClone)];
				remainingDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesRemainingPricePerDayClone), getBreakdownInSortedOrder(breakdownExpensesRemainingCostPerDayClone)];
				forecastDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesPlannedPricePerDay), getBreakdownInSortedOrder(breakdownExpensesPlannedCostPerDay)];
				actualsVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesPriceActualVsPlanPerDay), getBreakdownInSortedOrder(breakdownExpensesCostActualVsPlanPerDay)];
				forecastVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesPriceForecastVsPlanPerDay), getBreakdownInSortedOrder(breakdownExpensesCostForecastVsPlanPerDay)];
				if (hasBaseline) {
					const breakdownExpensesPriceBaselineVsActualWorkPerDay = createMilestoneExpenseCategoryVariance(breakdownBaselineExpensesPricePerDayClone, breakdownExpensesActualsPricePerDayClone, subtract);
					const breakdownExpensesCostBaselineVsActualWorkPerDay = createMilestoneExpenseCategoryVariance(breakdownBaselineExpensesCostPerDayClone, breakdownExpensesActualsCostPerDayClone, subtract);
					const breakdownExpensesPriceBaselineVsForecastWorkPerDay = createMilestoneExpenseCategoryVariance(breakdownBaselineExpensesPricePerDayClone, breakdownExpensesPlannedPricePerDay, subtract);
					const breakdownExpensesCostBaselineVsForecastWorkPerDay = createMilestoneExpenseCategoryVariance(breakdownBaselineExpensesCostPerDayClone, breakdownExpensesPlannedCostPerDay, subtract);
					baselineDataPerDay = [getBreakdownInSortedOrder(breakdownBaselineExpensesPricePerDayClone), getBreakdownInSortedOrder(breakdownBaselineExpensesCostPerDayClone)];
					baselineVsActualsDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesPriceBaselineVsActualWorkPerDay), getBreakdownInSortedOrder(breakdownExpensesCostBaselineVsActualWorkPerDay)];
					baselineVsForecastDataPerDay = [getBreakdownInSortedOrder(breakdownExpensesPriceBaselineVsForecastWorkPerDay), getBreakdownInSortedOrder(breakdownExpensesCostBaselineVsForecastWorkPerDay)];
				}
				break;
			case TABLE_TABS.INVOICE:
				actualsDataPerDay = [priceActualPerDay, costActualPerDay];
				invoicedDataPerDay = [invoicedPerDay, totalPaymentsPerDay];
				break;
			default:
				const breakdownPricePlannedPerDay = createMilestoneRoleVariance(breakdownPriceActualPerDayClone, breakdownPriceRemainingPerDayClone, add);
				const breakdownCostForecastPerDay = createMilestoneRoleVariance(breakdownCostActualPerDayClone, breakdownCostRemainingPerDayClone, add);
				const breakdownPriceActualVsPlanPerDay = createMilestoneRoleVariance(breakdownPriceActualPerDayClone, breakdownPricePlanPerDayClone, subtract);
				const breakdownCostActualVsPlanPerDay = createMilestoneRoleVariance(breakdownCostActualPerDayClone, breakdownCostPlanPerDayClone, subtract);
				const breakdownPriceForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownPricePlannedPerDay, breakdownPricePlanPerDayClone, subtract);
				const breakdownCostForecastVsPlanPerDay = createMilestoneRoleVariance(breakdownCostForecastPerDay, breakdownCostPlanPerDayClone, subtract);
				actualsDataPerDay = [getBreakdownInSortedOrder(breakdownPriceActualPerDayClone), getBreakdownInSortedOrder(breakdownCostActualPerDayClone)];
				planDataPerDay = [getBreakdownInSortedOrder(breakdownPricePlanPerDayClone), getBreakdownInSortedOrder(breakdownCostPlanPerDayClone)];
				remainingDataPerDay = [getBreakdownInSortedOrder(breakdownPriceRemainingPerDayClone), getBreakdownInSortedOrder(breakdownCostRemainingPerDayClone)];
				forecastDataPerDay = [getBreakdownInSortedOrder(breakdownPricePlannedPerDay), getBreakdownInSortedOrder(breakdownCostForecastPerDay)];
				actualsVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownPriceActualVsPlanPerDay), getBreakdownInSortedOrder(breakdownCostActualVsPlanPerDay)];
				forecastVsPlanDataPerDay = [getBreakdownInSortedOrder(breakdownPriceForecastVsPlanPerDay), getBreakdownInSortedOrder(breakdownCostForecastVsPlanPerDay)];
				if (hasBaseline) {
					const breakdownPriceBaselineVsActualPerDay = createMilestoneRoleVariance(breakdownBaselinePricePerDayClone, breakdownPriceActualPerDayClone, subtract);
					const breakdownCostBaselineVsActualPerDay = createMilestoneRoleVariance(breakdownBaselineCostPerDayClone, breakdownCostActualPerDayClone, subtract);
					const breakdownPriceBaselineVsForecastPerDay = createMilestoneRoleVariance(breakdownBaselinePricePerDayClone, breakdownPricePlannedPerDay, subtract);
					const breakdownCostBaselineVsForecastPerDay = createMilestoneRoleVariance(breakdownBaselineCostPerDayClone, breakdownCostForecastPerDay, subtract);
					baselineDataPerDay = [getBreakdownInSortedOrder(breakdownBaselinePricePerDayClone), getBreakdownInSortedOrder(breakdownBaselineCostPerDayClone)];
					baselineVsActualsDataPerDay = [getBreakdownInSortedOrder(breakdownPriceBaselineVsActualPerDay), getBreakdownInSortedOrder(breakdownCostBaselineVsActualPerDay)];
					baselineVsForecastDataPerDay = [getBreakdownInSortedOrder(breakdownPriceBaselineVsForecastPerDay), getBreakdownInSortedOrder(breakdownCostBaselineVsForecastPerDay)];
				}
				break;
		}

		let baselineTableDataPromise, baselineVsActualsTableDataPromise, baselineVsForecastTableDataPromise, forecastTableDataPromise;
		if (selectedTablesTab !== TABLE_TABS.INVOICE) {
			if (isMoneyTabSelected) {
				if (hasBaseline) {
					baselineTableDataPromise = getTableData('baselineDataPerDayMoney', baselineDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab));
					baselineVsActualsTableDataPromise = getTableData('baselineVsActualsDataPerDayMoney', baselineVsActualsDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab));
					baselineVsForecastTableDataPromise = getTableData('baselineVsForecastDataPerDayMoney', baselineVsForecastDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab));
				}
				forecastTableDataPromise = getTableData('forecastDataPerDayMoney', forecastDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab));
			} else {
				if (hasBaseline) {
					baselineTableDataPromise = getTableData('baselineDataPerDay', baselineDataPerDay, null, false, true);
					baselineVsActualsTableDataPromise = getTableData('baselineVsActualsDataPerDay', baselineVsActualsDataPerDay, null, false, true);
					baselineVsForecastTableDataPromise = getTableData('baselineVsForecastDataPerDay', baselineVsForecastDataPerDay, null, false, true);
				}
				forecastTableDataPromise = getTableData('forecastDataPerDay', forecastDataPerDay, null, false, true);
			}
		} else {
			baselineTableDataPromise = null;
			baselineVsActualsTableDataPromise = null;
			baselineVsForecastTableDataPromise = null;
			forecastTableDataPromise = null;
		}

		const actualsTableDataPromise = isMoneyTabSelected
			? getTableData('actualsDataPerDayMoney', actualsDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab))
			: getTableData('actualsDataPerDay', actualsDataPerDay, null, false, true);

		const planTableDataPromise =
			selectedTablesTab !== TABLE_TABS.INVOICE
				? isMoneyTabSelected
					? getTableData('planDataPerDayMoney', planDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab))
					: getTableData('planDataPerDayMinutes', planDataPerDay, [formatMessage({id: 'common.estimated_scope'})], false, true)
				: !isMoneyTabSelected //if invoice tab is selected but the time unit is also selected
				? getTableData('planDataPerDayMinutes', planDataPerDay, [formatMessage({id: 'common.estimated_scope'})], false, true)
				: null;

		const remainingTableDataPromise =
			selectedTablesTab !== TABLE_TABS.INVOICE
				? isMoneyTabSelected
					? getTableData('remainingDataPerDayMoney', remainingDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab))
					: getTableData('remainingDataPerDayMinutes', remainingDataPerDay, [formatMessage({id: 'insights.component.hours.sprintBurndown'})], false, true)
				: !isMoneyTabSelected //if invoice tab is selected but the time unit is also selected
				? getTableData('remainingDataPerDayMinutes', remainingDataPerDay, [formatMessage({id: 'insights.component.hours.sprintBurndown'})], false, true)
				: null;

		const actualsVariancePlanTableDataPromise =
			selectedTablesTab !== TABLE_TABS.INVOICE
				? isMoneyTabSelected
					? getTableData('actualsVsPlanDataPerDayMoney', actualsVsPlanDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab))
					: getTableData('actualsVsPlanDataPerDayMinutes', actualsVsPlanDataPerDay, [formatMessage({id: 'common.variance'})], false, true)
				: !isMoneyTabSelected //if invoice tab is selected but the time unit is also selected
				? getTableData('actualsVsPlanDataPerDayMinutes', actualsVsPlanDataPerDay, [formatMessage({id: 'common.variance'})], false, true)
				: null;

		const forecastVariancePlanTableDataPromise =
			selectedTablesTab !== TABLE_TABS.INVOICE
				? isMoneyTabSelected
					? getTableData('forecastVsPlanDataPerDayMoney', forecastVsPlanDataPerDay, null, true, ![TABLE_TABS.INVOICE].includes(selectedTablesTab))
					: getTableData('forecastVsPlanDataPerDayMinutes', forecastVsPlanDataPerDay, [formatMessage({id: 'common.variance'})], false, true)
				: !isMoneyTabSelected //if invoice tab is selected but the time unit is also selected
				? getTableData('forecastVsPlanDataPerDayMinutes', forecastVsPlanDataPerDay, [formatMessage({id: 'common.variance'})], false, true)
				: null;

		let baselineTableData = null,
			actualsTableData = null,
			planTableData = null,
			remainingTableData = null,
			actualsVariancePlanTableData = null,
			forecastTableData = null,
			forecastVariancePlanTableData = null,
			baselineVarianceToActualsTableData = null,
			baselineVarianceToForecastTableData = null,
			invoiceTableData = null,
			invoiceVarianceActualTableData = null;

		Promise.all([
			baselineTableDataPromise,
			actualsTableDataPromise,
			planTableDataPromise,
			remainingTableDataPromise,
			forecastTableDataPromise,
			baselineVsActualsTableDataPromise,
			baselineVsForecastTableDataPromise,
			actualsVariancePlanTableDataPromise,
			forecastVariancePlanTableDataPromise,
		])
			.then(
				([
					baselineTableDataResponse,
					actualsTableDataResponse,
					planTableDataResponse,
					remainingTableDataResponse,
					forecastTableDataResponse,
					baselineVsActualsTableDataResponse,
					baselineVsForecastTableDataResponse,
					actualsVariancePlanTableDataResponse,
					forecastVariancePlanTableDataResponse,
				]) => {
					baselineTableData = baselineTableDataResponse;
					actualsTableData = actualsTableDataResponse;
					planTableData = planTableDataResponse;
					remainingTableData = remainingTableDataResponse;
					forecastTableData = forecastTableDataResponse;
					actualsVariancePlanTableData = actualsVariancePlanTableDataResponse;
					baselineVarianceToActualsTableData = baselineVsActualsTableDataResponse;
					baselineVarianceToForecastTableData = baselineVsForecastTableDataResponse;
					forecastVariancePlanTableData = forecastVariancePlanTableDataResponse;

					let invoiceDataPromise = null,
						actualsDataPromise = null;

					if (selectedTablesTab === TABLE_TABS.INVOICE && isMoneyTabSelected) {
						invoiceDataPromise = getTableData('invoicedDataPerDay', invoicedDataPerDay, [formatMessage({id: 'common.invoiced'}), formatMessage({id: 'settings_subscription.paid'})], false, false);
						actualsDataPromise = getTableData('actualsDataPerDay', actualsDataPerDay, null, true, ![TABLE_TABS.EXPENSES, TABLE_TABS.INVOICE].includes(selectedTablesTab));
					}

					return Promise.all([invoiceDataPromise, actualsDataPromise]);
				}
			)
			.then(([invoiceDataResponse, actualsDataResponse]) => {
				let invoiceVarianceActualTableDataPromise = null;

				// INVOICE SECTION
				if (selectedTablesTab === TABLE_TABS.INVOICE && isMoneyTabSelected) {
					invoiceTableData = invoiceDataResponse[0];
					invoiceVarianceActualTableDataPromise = getInvoiceVarianceToActualTableData(
						actualsDataResponse[1][0],
						invoiceDataResponse[1][1].map(cell => [cell[0]]),
						actualsDataResponse[1][1].map(cell => [cell[0]])
					);
				}

				return Promise.all([invoiceVarianceActualTableDataPromise]);
			})
			.then(([invoiceVarianceActualTableDataResponse]) => {
				invoiceVarianceActualTableData = invoiceVarianceActualTableDataResponse;
				// Add some additional breadcrumbs to a potential sentry error
				Sentry.addBreadcrumb({
					data: {
						baselineTableData,
						actualsTableData,
						planTableData,
						remainingTableData,
						actualsVariancePlanTableData,
						forecastTableData,
						forecastVariancePlanTableData,
						baselineVarianceToActualsTableData,
						baselineVarianceToForecastTableData,
						invoiceTableData,
						invoiceVarianceActualTableData,
					},
					category: 'tablesData',
					level: Sentry.Severity.Debug,
				});
				setTablesData({
					baselineTableData,
					actualsTableData,
					planTableData,
					remainingTableData,
					actualsVariancePlanTableData,
					forecastTableData,
					forecastVariancePlanTableData,
					baselineVarianceToActualsTableData,
					baselineVarianceToForecastTableData,
					invoiceTableData,
					invoiceVarianceActualTableData,
					locks,
				});
				setIsTableDataLoading(false);
			})
			// eslint-disable-next-line no-console
			.catch(e => console.error(e));
	}, [isMoneyTabSelected, theEyeOptions, selectedTablesTab, missingProjectDates, project, selectedPeriod, isTableDataLoading]);

	const billableExpenseItems = expenseItems && expenseItems.edges.filter(edge => edge.node.approved && edge.node.billable).map(edge => edge.node);
	const today = moment();

	const billableExpensesTotal = billableExpenseItems.reduce((total, billableItem) => {
		const expenseDate = Util.CreateNonUtcMomentDate(billableItem.expenseYear, billableItem.expenseMonth, billableItem.expenseDay);
		const isPast = expenseDate && expenseDate.isBefore(today, 'day');

		return total + (isPast ? billableItem.price * billableItem.quantity : 0);
	}, 0);

	const invoiceAmountTotal = invoicedPerDay.reduce((total, invoiced) => total + invoiced.value, 0);
	const unInvoicedTotalAmount = project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS ? financialServiceUnInvoicedTotal : priceActualPerDay.reduce((total, acc) => total + acc.value, 0) - invoiceAmountTotal;
	const invoicePaid = totalPaymentsPerDay.reduce((total, paid) => total + paid.value, 0);

	const {lockedWorkTotal, lockedExpenseTotal, lockedOffsetTotal, isUsingLocks} = isFixedPriceV2 ? getLockedAmountTotal(locks, breakdownPriceActualPerDay) : {lockedWorkTotal: 0, lockedExpenseTotal: 0, lockedOffsetTotal: 0, isUsingLocks: false};

	const addProjectDates = () => {
		showModal({
			type: MODAL_TYPE.ADD_PROJECT_DATES,
			project,
			datesSetCallback: retry,
		});
	};

	// Segment
	useEffect(() => {
		const activeChartTablesTab = localStorage.getItem('activeChartTablesTabBudgetV2') ? JSON.parse(localStorage.getItem('activeChartTablesTabBudgetV2')) : {};
		const chartToRender = activeChartTablesTab[isMoneyTabSelected ? selectedTablesTab : selectedTablesTab + '_time'];

		const pageProperties = {
			Tab: isMoneyTabSelected ? 'Money' : 'Time',
		};
		if (TABLE_TAB_TRACKING_LABELS[selectedTablesTab]) {
			pageProperties.Table = TABLE_TAB_TRACKING_LABELS[selectedTablesTab];
		}
		if (BUDGET_CHART_TRACKING_LABELS[chartToRender]) {
			pageProperties.Chart = BUDGET_CHART_TRACKING_LABELS[chartToRender];
		}

		tracking.trackPage('Project-budget', null, null, pageProperties);
	}, [isMoneyTabSelected, selectedTablesTab]);

	useEffect(() => {
		if (theEyeOptions) {
			tracking.trackEyeSelector(theEyeOptions);
		}
	}, [theEyeOptions]);

	return (
		<div className="project-page-wrapper">
			{showFixedPriceUpdateToast ? <FixedPriceUpdateToast /> : null}
			{!project.financialDataAvailability ? <FinancialServiceAvailabilityToast /> : null}
			<div className={'projects-page-body' + (hasFeatureFlag('third_level_nav') ? ' third-level' : '')}>
				{showFixedPrice ? <FixedPriceContainer viewer={viewer} toggleFixedPrice={toggleFixedPrice} getFormattedNumberWithCurrency={getFormattedNumberWithCurrency} /> : null}

				<div className={`header-container`}>{getHeader()}</div>
				<CustomScrollDiv horizontalandverticalcontent="true" onScrollFrame={data => handleScroll(data)}>
					<div className="scrollable" style={{marginTop: `${scrollTop > headerTop ? headerRef.current.getBoundingClientRect().height : '0'}px`}}>
						<div className="top-sections">
							<div className={'budget-data' + (!project.billable || !isMoneyTabSelected ? ' full-size' : '')}>
								<div className="section-title">{formatMessage({id: 'project_budget.budget_data'})}</div>
								{useMemo(
									() =>
										missingProjectDates ? (
											<div className="chart empty">
												<GenericEmptyState text={''} header={formatMessage({id: 'project_budget.project_has_no_start_or_end_date'})} callback={addProjectDates} actionText={formatMessage({id: 'project_budget.add_dates'})} />
											</div>
										) : (
											<BudgetCharts
												costActualPerDay={costActualPerDay}
												costPlannedPerDay={costPlannedPerDay}
												costRemainingPerDay={costRemainingPerDay}
												priceActualPerDay={priceActualPerDay}
												pricePlannedPerDay={pricePlannedPerDay}
												priceRemainingPerDay={priceRemainingPerDay}
												invoicedPerDay={invoicedPerDay}
												totalPaymentsPerDay={totalPaymentsPerDay}
												registeredMinutesPerDay={registeredMinutesPerDay}
												plannedMinutesPerDay={plannedMinutesPerDay}
												remainingMinutesPerDay={remainingMinutesPerDay}
												costPlannedWorkPerDay={costPlannedWorkPerDay}
												pricePlannedWorkPerDay={pricePlannedWorkPerDay}
												costRemainingWorkPerDay={costRemainingWorkPerDay}
												priceRemainingWorkPerDay={priceRemainingWorkPerDay}
												costActualWorkPerDay={costActualWorkPerDay}
												priceActualWorkPerDay={priceActualWorkPerDay}
												pricePlannedExpensesPerDay={pricePlannedExpensesPerDay}
												costPlannedExpensesPerDay={costPlannedExpensesPerDay}
												priceActualExpensesPerDay={priceActualExpensesPerDay}
												costActualExpensesPerDay={costActualExpensesPerDay}
												priceRemainingExpensesPerDay={priceRemainingExpensesPerDay}
												costRemainingExpensesPerDay={costRemainingExpensesPerDay}
												baselinePricePerDay={baselinePricePerDay}
												baselineCostPerDay={baselineCostPerDay}
												baselinePriceWorkPerDay={baselinePriceWorkPerDay}
												baselinePriceExpensesPerDay={baselinePriceExpensesPerDay}
												baselineCostWorkPerDay={baselineCostWorkPerDay}
												baselineCostExpensesPerDay={baselineCostExpensesPerDay}
												billable={project.billable}
												isShowingWeekNumber={tableNumbersFilterOptions.weekNumber}
												isMoneyTabSelected={isMoneyTabSelected}
												currencySymbol={currencySymbol}
												filters={theEyeOptions}
												isInvoiceTableTabSelected={selectedTablesTab === TABLE_TABS.INVOICE}
												project={project}
												isEstimatedInNewPoints={isEstimatedInNewPoints}
												selectedTablesTab={selectedTablesTab}
												estimationUnit={estimationUnit}
												minutesPerEstimationPoint={minutesPerEstimationPoint}
												periodInTable={periodInTable}
												showBaseline={hasBaseline}
												isFixedPrice={isFixedPrice}
												isFixedPriceV2={isFixedPriceV2}/>
										),
									[tablesData, missingProjectDates]
								)}
							</div>

							{project.billable && isMoneyTabSelected ? (
								<ProjectFinancials
									currencySymbol={currencySymbol}
									project={project}
									tooltip={fixedPriceProjectFinancialsTooltip}
									isFixedPrice={isFixedPrice}
									isFixedPriceV2={isFixedPriceV2}
									billableExpensesTotal={billableExpensesTotal}
									invoiceAmountTotal={invoiceAmountTotal}
									unInvoicedTotal={unInvoicedTotalAmount}
									invoicePaid={invoicePaid}
									isUsingLocks={isUsingLocks}
									lockedWorkTotal={lockedWorkTotal}
									lockedExpenseTotal={lockedExpenseTotal}
									lockedOffsetTotal={lockedOffsetTotal}
									valueBasedProgress={project.valueBasedProgress}
								/>
							) : null}
						</div>
						<div ref={projectTotalsDiv} className={`project-totals ${scrollTop > projectTotalsTop && shouldTotalsBeStuck ? 'stuck' : ''}`}>
							<div className="section-title" onClick={() => toggleTotals()} data-userpilot="budget-totals-expand-button">
								{formatMessage({id: 'project_budget.project_totals'})}
								<ExpandIcon expanded={isTotalsExpanded} />
							</div>
							{useMemo(
								() =>
									isTotalsExpanded ? (
										<BudgetTable
											test
											data={formatTableData(getTotalsTableData())}
											projectTotals
											color={'#f5f5f8'}
											highlightColumnIndex={isMoneyTabSelected ? (hasBaseline ? 1 : isFixedPriceV2 ? 9 : isFixedPrice && !hasBaseline ? 4 : 5) : hasBaseline ? 1 : 4}
											lockedColumnIndexMap={lockedColumnIndexMap}
											columnTooltipText={
												isMoneyTabSelected
													? [
															null,
															baselineTooltipTextTotals,
															baselineVarianceToActualsTooltipTextTotals,
															baselineVarianceToForecastTooltipTextTotals,
															fixedPriceTooltipTextTotals,
															actualsTooltipTextTotals,
															planTooltipTextTotals,
															actualVarianceToPlanTooltipTextTotals,
															remainingTooltipTextTotals,
															forecastTooltipTextTotals,
															forecastVarianceToPlanTooltipTextTotals,
													  ]
													: [
															null,
															baselineTooltipTextTotals,
															baselineVarianceToActualsTooltipTextTotals,
															baselineVarianceToForecastTooltipTextTotals,
															actualsTooltipTextTotals,
															planTooltipTextTotals,
															actualVarianceToPlanTooltipTextTotals,
															remainingTooltipTextTotals,
															forecastTooltipTextTotals,
															forecastVarianceToPlanTooltipTextTotals,
													  ]
											}
											currencySymbol={currencySymbol}
										/>
									) : null,
								[tablesData, isTotalsExpanded, isFixedPrice]
							)}
						</div>
						<TablesSection
							retry={retry}
							isFixedPriceV2={isFixedPriceV2}
							isTableDataLoading={isTableDataLoading}
							tablesData={tablesData}
							isMoneyTabSelected={isMoneyTabSelected}
							marginTop={`${scrollTop > projectTotalsTop && shouldTotalsBeStuck && projectTotalsDiv && projectTotalsDiv.current ? projectTotalsDiv.current.getBoundingClientRect().height : '0'}px`}
							tableContainerRef={tableContainerRef}
							tablesSectionRef={tablesSectionRef}
							areTablesOutOfScreen={areTablesOutOfScreen}
							theEyeOptions={theEyeOptions}
							formatTableData={formatTableData}
							tooltipsText={{
								actualsTooltipText,
								planTooltipText,
								actualVarianceToPlanTooltipText,
								remainingTooltipText,
								forecastTooltipText,
								forecastVarianceToPlanTooltipText,
								invoiceVarianceToActualTooltipText,
								baselineTooltipText,
								baselineVarianceToActualsTooltipText,
								baselineVarianceToForecastTooltipText,
							}}
							selectedTablesTab={selectedTablesTab}
							handleTablesTabSelected={handleTablesTabSelected}
							missingProjectDates={missingProjectDates}
							addProjectDates={addProjectDates}
							project={project}
							todayPeriodIndex={todayPeriodIndex}
							lockedColumnIndexMap={lockedColumnIndexMap}
							disabledColumnIndexes={disabledColumnIndexes}
							currencySymbol={currencySymbol}
							setShowFixedPriceUpdateToast={setShowFixedPriceUpdateToast}
						/>
					</div>
				</CustomScrollDiv>
			</div>
		</div>
	);
};

export default injectIntl(ProjectPage);
